<template>
  <div class="pharma-home">
    <!-- 顶部导航组件 -->
    <TheHeader />

    <!-- 创新液态流动轮播图区域 -->
    <div class="hero-section">
      <!-- 加载中状态 -->
      <div v-if="bannerLoading" class="loading-wrapper">
        <div class="liquid-loading">
          <div class="liquid-wave"></div>
          <div class="liquid-wave"></div>
          <div class="liquid-wave"></div>
        </div>
        <span class="loading-text">数据流动中...</span>
      </div>

      <!-- 加载错误状态 -->
      <div v-else-if="bannerError" class="error-wrapper">
        <div class="error-animation">
          <svg width="80" height="80" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
          </svg>
        </div>
        <span>轮播图加载失败，请刷新重试</span>
      </div>

      <!-- 创新轮播图内容 -->
      <div v-else class="liquid-carousel-wrapper">
        <!-- 背景液态效果 -->
        <div class="liquid-background">
          <div class="liquid-blob" v-for="n in 6" :key="n"></div>
        </div>

        <!-- 3D立体轮播容器 -->
        <div class="pyramid-carousel-container">
          <!-- 主要轮播内容 -->
          <div class="pyramid-carousel" ref="pyramidCarousel">
            <div
              v-for="(banner, index) in bannerList"
              :key="banner.id"
              class="pyramid-slide"
              :class="{
                active: index === currentSlide,
                prev: index === getPrevIndex(),
                next: index === getNextIndex(),
                far: Math.abs(index - currentSlide) > 1
              }"
              :style="getSlideStyle(index)"
            >
              <!-- 立体卡片容器 -->
              <div class="slide-card">
                <!-- 图片层 -->
                <div class="slide-image-layer">
                <img :src="banner.image_url" :alt="banner.title" />
                  <div class="image-ripple"></div>
              </div>

                <!-- 液态遮罩层 -->
                <div class="liquid-mask">
                  <svg class="liquid-svg" viewBox="0 0 400 200">
                    <path class="liquid-path" />
                  </svg>
                </div>

                <!-- 内容层 -->
                <div class="slide-content-layer">
                <div class="content-container">
                  <h2 class="slide-title">{{ banner.title || '专业品质 · 呵护健康' }}</h2>
                  <p class="slide-description">{{ banner.description || '北京佰智康医疗科技，为您提供优质可靠的医疗产品与服务' }}</p>
                    <div class="slide-actions">
                      <router-link :to="{ name: 'Products' }" class="liquid-button">
                        <span class="button-text">探索产品</span>
                        <div class="button-liquid"></div>
                        <svg class="button-arrow" width="20" height="20" viewBox="0 0 24 24">
                          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
                        </svg>
                    </router-link>
                  </div>
                  </div>
                </div>

                <!-- 装饰性粒子 -->
                <div class="slide-particles">
                  <div class="particle" v-for="n in 8" :key="n" :style="getParticleStyle(n)"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 创新控制界面 -->
          <div class="control-interface">
            <!-- 侧边导航 -->
            <div class="side-navigation">
            <div
              v-for="(banner, index) in bannerList"
              :key="banner.id"
                class="nav-dot"
              :class="{ active: index === currentSlide }"
              @click="goToSlide(index)"
              >
                <div class="dot-core"></div>
                <div class="dot-ring"></div>
                <div class="dot-pulse"></div>
              </div>
            </div>

            <!-- 底部进度条 -->
            <div class="progress-bar">
              <div class="progress-track">
                <div 
                  class="progress-fill" 
                  :style="{ width: `${((currentSlide + 1) / bannerList.length) * 100}%` }"
            ></div>
          </div>
              <div class="progress-info">
                <span class="current-index">{{ String(currentSlide + 1).padStart(2, '0') }}</span>
                <span class="separator">/</span>
                <span class="total-count">{{ String(bannerList.length).padStart(2, '0') }}</span>
        </div>
      </div>

            <!-- 3D控制按钮 -->
            <button class="control-button control-prev" @click="prevSlide">
              <div class="button-face">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
                </svg>
    </div>
              <div class="button-shadow"></div>
            </button>

            <button class="control-button control-next" @click="nextSlide">
              <div class="button-face">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
                </svg>
              </div>
              <div class="button-shadow"></div>
            </button>
          </div>

          <!-- 环境光效 -->
          <div class="ambient-lights">
            <div class="light-beam light-1"></div>
            <div class="light-beam light-2"></div>
            <div class="light-beam light-3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创新重力悬浮药品展示区域 -->
    <section class="medicine-showcase">
      <!-- 全息投影背景 -->
      <div class="holographic-background">
        <div class="holo-grid">
          <div class="grid-line" v-for="i in 20" :key="'h-' + i"></div>
        </div>
        <div class="holo-particles">
          <div class="holo-particle" v-for="n in 15" :key="'p-' + n" :style="getHoloParticleStyle(n)"></div>
        </div>
        <div class="quantum-field">
          <div class="quantum-wave" v-for="w in 5" :key="'w-' + w"></div>
        </div>
      </div>

      <!-- 磁性标题区域 -->
      <div class="showcase-header">
        <div class="magnetic-title-container">
          <div class="title-field">
            <div class="field-distortion"></div>
            <h2 class="gravity-title">
              <span class="title-fragment" v-for="(char, index) in '精选药品推荐'.split('')" :key="index" :style="getTitleFragmentStyle(index)">
                {{ char }}
              </span>
            </h2>
            <p class="subtitle-hologram">专业品质，值得信赖的医疗产品</p>
            <router-link :to="{ name: 'Products' }" class="quantum-btn">
              <div class="btn-core">
                <span class="btn-text">查看全部</span>
                <div class="btn-energy"></div>
          </div>
              <div class="btn-field"></div>
              <svg class="btn-arrow" width="20" height="20" viewBox="0 0 24 24">
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
            </svg>
          </router-link>
          </div>
        </div>
      </div>

      <!-- 加载状态 - 重力模拟 -->
      <div v-if="medicineLoading" class="gravity-loading">
        <div class="gravity-field">
          <div class="mass-center"></div>
          <div class="orbit-particle" v-for="n in 8" :key="'orbit-' + n" :style="getOrbitStyle(n)"></div>
        </div>
        <p class="loading-text-holo">药品数据正在重力聚合中...</p>
      </div>

      <!-- 错误提示 - 量子故障 -->
      <div v-else-if="medicineError" class="quantum-error">
        <div class="error-field">
          <div class="error-distortion"></div>
          <svg class="error-icon" width="80" height="80" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
          </svg>
        </div>
        <p>{{ medicineError }}</p>
        <button class="quantum-retry-btn" @click="loadMedicineData">重新连接</button>
      </div>

      <!-- 无数据提示 -->
      <div v-else-if="medicineList.length === 0" class="void-space">
        <div class="void-field">
          <div class="void-ripple" v-for="r in 3" :key="'ripple-' + r"></div>
        </div>
        <p class="void-text">暂无药品数据存在于此维度</p>
      </div>

      <!-- 重力悬浮药品网格 -->
      <div v-else class="gravity-medicine-grid" ref="medicineGrid" @mousemove="handleGridMouseMove">
        <div 
          class="floating-medicine-card" 
          v-for="(product, index) in medicineList.slice(0, 6)" 
          :key="product.id"
          :ref="'medicineCard' + index"
          :style="getCardFloatingStyle(index)"
          @mouseenter="handleCardHover(index, $event)"
          @mouseleave="handleCardLeave(index)"
          @click="showPaymentDialog(product)"
        >
          <!-- 重力场可视化 -->
          <div class="gravity-field-viz">
            <div class="field-line" v-for="line in 6" :key="'line-' + line"></div>
          </div>

          <!-- 悬浮卡片容器 -->
          <div class="card-hull">
            <!-- 全息边框 -->
            <div class="holo-border">
              <div class="border-segment" v-for="seg in 4" :key="'seg-' + seg"></div>
            </div>

            <!-- 药品图像层 -->
            <div class="medicine-image-layer">
              <div class="image-portal">
            <img :src="getFullImageUrl(product.image) || placeholderImages.med1" :alt="product.name" />
                <div class="portal-energy"></div>
            </div>
              <!-- 已购买标签 -->
              <div v-if="isProductPermitted(product.id)" class="purchased-badge">
                <span>✓ 已购买</span>
              </div>
              <div class="category-tag">
                <div class="tag-glow"></div>
                <span>{{ product.category ? product.category.name : '医疗健康' }}</span>
          </div>
            </div>

            <!-- 信息投影层 -->
            <div class="info-projection">
              <div class="projection-container">
                <h3 class="medicine-name">
                  <span class="name-glow"></span>
                  {{ product.name }}
                </h3>
                
                <div class="data-streams">
                  <div class="data-stream" v-if="product.manufacturer">
                    <div class="stream-label">生产厂家</div>
                    <div class="stream-value">{{ getShortManufacturer(product.manufacturer) }}</div>
                    <div class="stream-flow"></div>
              </div>
                  
                  <div class="data-stream" v-if="product.approval_number">
                    <div class="stream-label">批准文号</div>
                    <div class="stream-value">{{ product.approval_number }}</div>
                    <div class="stream-flow"></div>
              </div>
                  
                  <div class="data-stream description-stream" v-if="product.indications">
                    <div class="stream-label">适应症</div>
                    <div class="stream-value">{{ getShortDescription(product.description) || (product.indications ? getShortIndications(product.indications) : '暂无描述') }}</div>
                    <div class="stream-flow"></div>
              </div>
              
                  <!-- <div class="data-stream price-stream">
                    <div class="stream-label">查看详情</div>
                    <div class="stream-value price-value">¥{{ product.price }}</div>
                    <div class="stream-flow"></div>
              </div> -->
            </div>

                <!-- 量子传送按钮 -->
                <!-- <div class="teleport-zone">
                  <div class="teleport-btn" @click.stop="showPaymentDialog(product)">
                    <div class="teleport-core">
                      <span>付费查看</span>
                      <div class="teleport-energy"></div>
                    </div>
                    <div class="teleport-field"></div>
                  </div>
                </div> -->
              </div>
            </div>

            <!-- 重力扭曲效果 -->
            <div class="gravity-distortion">
              <div class="distortion-ring" v-for="ring in 3" :key="'ring-' + ring"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 量子分页器 -->
      <div class="quantum-pagination" v-if="medicinePagination.total > 0">
        <div class="pagination-field">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="medicinePagination.total"
          :page-size="medicinePagination.pageSize"
          :current-page="medicinePagination.currentPage"
          @current-change="handleCurrentChange"
        />
        </div>
      </div>
    </section>

    <!-- 神经网络数据流关于我们区域 -->
    <section class="neural-about-section" ref="neuralSection">
      <!-- 神经网络背景 -->
      <div class="neural-network-bg">
        <!-- 动态连接节点 -->
        <div class="neural-nodes">
          <div 
            class="neural-node" 
            v-for="node in 50" 
            :key="'node-' + node"
            :style="getNeuralNodeStyle(node)"
          ></div>
        </div>

        <!-- 数据流连接线 -->
        <div class="neural-connections">
          <svg class="connections-svg" width="100%" height="100%">
            <path 
              v-for="(connection, index) in neuralConnections" 
              :key="'connection-' + index"
              :d="connection.path" 
              class="connection-line"
              :style="{ animationDelay: connection.delay + 's' }"
            />
          </svg>
        </div>

        <!-- 量子粒子场 -->
        <div class="quantum-particles">
          <div 
            class="quantum-particle" 
            v-for="particle in 30" 
            :key="'particle-' + particle"
            :style="getQuantumParticleStyle(particle)"
          ></div>
              </div>
            </div>

      <div class="neural-container">
        <!-- 加载状态 - 神经脉冲 -->
        <div v-if="aboutUsLoading" class="neural-loading">
          <div class="pulse-core">
            <div class="pulse-ring" v-for="ring in 4" :key="'ring-' + ring"></div>
          </div>
          <div class="loading-text-neural">神经网络正在建立连接...</div>
              </div>

        <!-- 错误状态 - 系统故障 -->
        <div v-else-if="aboutUsError" class="neural-error">
          <div class="error-disturbance">
            <div class="disturbance-wave" v-for="wave in 3" :key="'wave-' + wave"></div>
            <el-icon class="error-icon-neural"><WarningFilled /></el-icon>
                </div>
          <div class="error-text-neural">{{ aboutUsError }}</div>
          <button class="neural-retry-btn" @click="loadAboutUsData">
            <span class="btn-text">重新连接</span>
            <div class="btn-neural-glow"></div>
          </button>
                </div>

        <!-- 神经网络内容主体 -->
        <div v-else class="neural-content-matrix">
          <!-- 全息标题投影 -->
          <div class="holographic-header">
            <div class="holo-title-container">
              <div class="title-scanner-line"></div>
              <h2 class="neural-main-title">
                <span 
                  v-for="(char, index) in (aboutUsData.title || '佰智康医疗科技').split('')" 
                  :key="'char-' + index"
                  class="title-char"
                  :style="{ animationDelay: (index * 0.1) + 's' }"
                >{{ char }}</span>
              </h2>
                </div>
              </div>

          <!-- 信息流矩阵 -->
          <div class="info-flow-matrix">
            <!-- 主要信息流 -->
            <div class="primary-info-stream">
              <div class="stream-header">
                <div class="stream-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </div>
                <span class="stream-title">核心信息流</span>
                <div class="stream-flow-indicator"></div>
                </div>

              <div class="info-content-neural">
                <div class="neural-text-block" v-if="aboutUsData.content" v-html="sanitizeHtml(aboutUsData.content)"></div>
                <div class="neural-text-block" v-else>
                  <p class="neural-paragraph">北京佰智康医疗科技有限公司，运用先进的医疗科技和数据分析技术，为全球医疗健康领域提供高品质的解决方案。</p>
                  <p class="neural-paragraph">通过智能化数据管理系统，我们的产品在心脑血管、神经系统、免疫调节等医疗领域实现了精准有效的应用。</p>
                  </div>
                </div>
                  </div>


            <!-- 神经传导行动区 -->
            <div class="neural-action-zone">
              <div class="action-neural-field">
                <router-link :to="{ name: 'About' }" class="neural-explore-btn">
                  <div class="btn-neural-core">
                    <span class="btn-neural-text">深度探索</span>
                    <div class="btn-data-flow">
                      <div class="flow-bit" v-for="bit in 6" :key="'bit-' + bit"></div>
                </div>
              </div>
                  <div class="btn-neural-aura"></div>
                  <div class="btn-quantum-trails">
                    <div class="trail" v-for="trail in 3" :key="'trail-' + trail"></div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 支付二维码弹窗 -->
    <div v-if="paymentDialogVisible" class="payment-dialog-overlay" @click="closePaymentDialog">
      <div class="payment-dialog" @click.stop>
        <div class="payment-header">
          <h3>付费查看详情</h3>
          <button class="close-btn" @click="closePaymentDialog">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div class="payment-content">
          <div class="product-info">
            <h4>{{ currentProduct.name }}</h4>
            <p class="price">¥ {{ currentPrice }}</p>
          </div>
          <div class="qr-code-container">
            <img :src="require('@/assets/zhifu.png')" alt="支付二维码" class="qr-code" />
            <p class="qr-tip">扫码支付后即可查看完整详情</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部信息组件 -->
    <TheFooter />
  </div>
</template>

<script>
// 导入Element Plus组件
import { ElIcon, ElPagination } from 'element-plus'
import { WarningFilled } from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'
// 导入占位图片
import { placeholderImages } from '@/assets/placeholder.js'
// 导入自定义组件
import TheHeader from './TheHeader.vue'
import TheFooter from './TheFooter.vue'
// 导入API
import { getBanners } from '@/api/bannerService'
// 导入药品和公司简介服务
import { getMedicines, getAboutUs, BASE_URL, getPermittedMedicines } from '@/api/config'
// 导入用户状态管理
import { getToken, isLoggedIn } from '@/utils/userStore'

export default {
  name: 'HelloWorld',
  components: {
    ElIcon,
    ElPagination,
    WarningFilled,
    TheHeader,
    TheFooter
  },
  data() {
    return {
      // 图片资源
      images: placeholderImages,
      // 轮播图数据（从API获取）
      bannerList: [],
      bannerLoading: true,
      bannerError: false,
      defaultDescription: '北京佰智康医疗科技，为您提供优质可靠的医疗产品',
      currentSlide: 0, // 当前轮播图索引
      fragmentingSlide: -1, // 正在破碎的轮播图索引
      assemblingSlide: -1, // 正在重组的轮播图索引
      autoplayTimer: null, // 自动播放定时器
      isTransitioning: false, // 是否正在过渡中
      // 药品相关数据
      medicineList: [],
      medicineLoading: false,
      medicineError: null,
      medicinePagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      // 公司简介数据
      aboutUsData: {},
      aboutUsLoading: false,
      aboutUsError: null,

      // 神经网络数据
      neuralConnections: [],
      
      // 支付弹窗相关数据
      paymentDialogVisible: false,
      currentProduct: {},
      currentPrice: 0,
      
      // 权限药品列表
      permittedMedicineIds: [],
    }
  },
  computed: {
    // ... existing code ...
  },
  methods: {
    // 加载轮播图数据
    async loadBannerData() {
      try {
        this.bannerLoading = true
        this.bannerError = false
        
        const data = await getBanners()
        
        // 过滤状态正常的轮播图
        this.bannerList = data.filter(item => item.status === 'normal')
        
        // 如果没有轮播图数据，使用默认图片
        if (this.bannerList.length === 0) {
          this.useDefaultBanners()
        }
        
        this.bannerLoading = false
      } catch (error) {
        console.error('轮播图加载错误:', error)
        this.bannerLoading = false
        this.bannerError = true
        
        // 出错时使用默认轮播图
        this.useDefaultBanners()
      }
    },
    
    // 使用默认轮播图数据（当API请求失败时）
    useDefaultBanners() {
      this.bannerList = [
        {
          id: 1,
          title: '专业品质 · 呵护健康',
          description: '北京佰智康医疗科技，为您提供优质可靠的医疗产品',
          url: '',
          image_url: placeholderImages.banner1,
          background_color: '#3a8ee6'
        },
        {
          id: 2,
          title: '全面呵护 · 健康生活',
          description: '多种医疗产品类型，满足您的各种健康需求',
          url: '',
          image_url: placeholderImages.banner2,
          background_color: '#42b983'
        },
        {
          id: 3,
          title: '专业团队 · 质量保障',
          description: '严格的质量控制流程，呵护您的健康',
          url: '',
          image_url: placeholderImages.banner3,
          background_color: '#e74c3c'
        }
      ]
      this.bannerError = false
      this.bannerLoading = false
    },
    
    // 加载药品数据
    async loadMedicineData() {
      this.medicineLoading = true;
      this.medicineError = null;
      
      try {
        const { currentPage, pageSize } = this.medicinePagination;
        const response = await getMedicines({
          page: currentPage,
          pagesize: pageSize
        });
        
        if (response.code === 1) {
          // 使用API返回的真实价格
          this.medicineList = response.data.data;
          this.medicinePagination.total = response.data.total;
        } else {
          this.medicineError = response.msg || '获取药品数据失败';
        }
      } catch (error) {
        console.error('获取药品数据错误:', error);
        this.medicineError = '网络错误，请稍后重试';
      } finally {
        this.medicineLoading = false;
      }
    },
    
    // 页码变化
    handleCurrentChange(page) {
      this.medicinePagination.currentPage = page;
      this.loadMedicineData();
    },

    // 提取短厂商名称
    getShortManufacturer(manufacturer) {
      if (!manufacturer) return '暂无';
      // 如果厂商名称太长，只显示前10个字符加...
      return manufacturer.length > 10 ? manufacturer.substring(0, 10) + '...' : manufacturer;
    },

    // 从描述HTML中提取纯文本短描述
    getShortDescription(description) {
      if (!description) return '';
      // 创建临时DOM元素来解析HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = description;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    },

    // 从适应症HTML中提取纯文本短描述
    getShortIndications(indications) {
      if (!indications) return '';
      // 创建临时DOM元素来解析HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = indications;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    },

    // 获取完整图片URL
    getFullImageUrl(path) {
      if (!path) return null;
      
      // 如果已经是完整URL则直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 否则拼接基础URL
      return `${BASE_URL}${path}`;
    },

    // 加载公司简介数据
    async loadAboutUsData() {
      this.aboutUsLoading = true;
      this.aboutUsError = null;

      try {
        const response = await getAboutUs();

        if (response.code === 1) {
          this.aboutUsData = response.data;
        } else {
          this.aboutUsError = response.msg || '获取公司简介失败';
        }
      } catch (error) {
        console.error('获取公司简介错误:', error);
        this.aboutUsError = '网络错误，请稍后重试';
      } finally {
        this.aboutUsLoading = false;
      }
    },

    // 创新液态轮播图控制方法
    goToSlide(index) {
      if (this.isTransitioning || index === this.currentSlide) return;
      this.transitionToSlide(index);
    },

    // 上一张
    prevSlide() {
      if (this.isTransitioning) return;
      const prevIndex = this.currentSlide === 0 ? this.bannerList.length - 1 : this.currentSlide - 1;
      this.transitionToSlide(prevIndex);
    },

    // 下一张
    nextSlide() {
      if (this.isTransitioning) return;
      const nextIndex = this.currentSlide === this.bannerList.length - 1 ? 0 : this.currentSlide + 1;
      this.transitionToSlide(nextIndex);
    },

    // 流畅的3D过渡效果
    async transitionToSlide(targetIndex) {
      if (this.isTransitioning) return;

      this.isTransitioning = true;
      
      // 触发液态过渡动画
      this.triggerLiquidTransition();
      
      // 等待过渡动画完成
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // 更新当前幻灯片
      this.currentSlide = targetIndex;

      // 更新液态路径动画
      this.updateLiquidPaths();

      this.isTransitioning = false;
    },

    // 获取前一个索引
    getPrevIndex() {
      return this.currentSlide === 0 ? this.bannerList.length - 1 : this.currentSlide - 1;
    },

    // 获取下一个索引
    getNextIndex() {
      return this.currentSlide === this.bannerList.length - 1 ? 0 : this.currentSlide + 1;
    },

    // 获取幻灯片样式
    getSlideStyle(index) {
      const diff = index - this.currentSlide;
      
      if (index === this.currentSlide) {
        return {
          transform: 'translateX(0) translateZ(100px) rotateY(0deg) scale(1)',
          opacity: 1,
          zIndex: 10
        };
      } else if (diff === 1 || diff === -(this.bannerList.length - 1)) {
        return {
          transform: 'translateX(100%) translateZ(-50px) rotateY(-25deg) scale(0.8)',
          opacity: 0.7,
          zIndex: 5
        };
      } else if (diff === -1 || diff === this.bannerList.length - 1) {
        return {
          transform: 'translateX(-100%) translateZ(-50px) rotateY(25deg) scale(0.8)',
          opacity: 0.7,
          zIndex: 5
        };
      } else {
        return {
          transform: `translateX(${diff > 0 ? 200 : -200}%) translateZ(-100px) rotateY(${diff > 0 ? -45 : 45}deg) scale(0.6)`,
          opacity: 0,
          zIndex: 1
        };
      }
    },


    // 触发液态过渡动画
    triggerLiquidTransition() {
      // 触发液态波动效果
      const liquidPaths = document.querySelectorAll('.liquid-path');
      liquidPaths.forEach(path => {
        path.style.animation = 'none';
        path.offsetHeight; // 触发重排
        path.style.animation = 'liquidWave 0.6s ease-in-out';
      });
    },

    // 更新液态路径动画
    updateLiquidPaths() {
      const liquidPaths = document.querySelectorAll('.liquid-path');
      liquidPaths.forEach((path, index) => {
        const delay = index * 0.1;
        path.style.animationDelay = `${delay}s`;
      });
    },

    // 获取粒子样式
    getParticleStyle(index) {
      const colors = ['#60a5fa', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
      const size = Math.random() * 6 + 4;
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      const duration = Math.random() * 4 + 3;
      const delay = Math.random() * 2;

      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${x}%`,
        top: `${y}%`,
        backgroundColor: colors[index % colors.length],
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`,
        boxShadow: `0 0 ${size * 2}px ${colors[index % colors.length]}40`
      };
    },

    // 获取全息粒子样式
    getHoloParticleStyle(index) {
      const colors = ['#00d4ff', '#ff00d4', '#d4ff00', '#ff4900', '#4900ff'];
      const size = Math.random() * 4 + 2;
      const x = Math.random() * 100;
      const y = Math.random() * 100;
      const duration = Math.random() * 8 + 6;
      const delay = Math.random() * 3;

      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${x}%`,
        top: `${y}%`,
        background: colors[index % colors.length],
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`,
        boxShadow: `0 0 ${size * 3}px ${colors[index % colors.length]}`,
        filter: `blur(${Math.random() * 2}px)`
      };
    },

    // 获取标题片段样式
    getTitleFragmentStyle(index) {
      const delay = index * 0.1;
      const randomOffset = (Math.random() - 0.5) * 2;
      
      return {
        animationDelay: `${delay}s`,
        transform: `translateY(${randomOffset}px)`,
        filter: `hue-rotate(${index * 20}deg)`
      };
    },

    // 获取轨道样式
    getOrbitStyle(index) {
      const angle = (index / 8) * 360;
      const radius = 40 + (index % 3) * 15;
      const duration = 3 + (index % 4);
      
      return {
        transform: `rotate(${angle}deg) translateX(${radius}px)`,
        animationDuration: `${duration}s`,
        animationDelay: `${index * 0.2}s`
      };
    },

    // 获取卡片悬浮样式
    getCardFloatingStyle(index) {
      const row = Math.floor(index / 3);
      const col = index % 3;
      const baseDelay = (row * 3 + col) * 0.2;
      const floatRange = 5 + Math.random() * 10;
      
      return {
        animationDelay: `${baseDelay}s`,
        '--float-range': `${floatRange}px`,
        '--rotation-range': `${Math.random() * 4 - 2}deg`
      };
    },

    // 处理网格鼠标移动
    handleGridMouseMove(event) {
      if (!this.$refs.medicineGrid) return;
      
      const rect = this.$refs.medicineGrid.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // 更新CSS自定义属性用于磁性效果
      this.$refs.medicineGrid.style.setProperty('--mouse-x', `${x}px`);
      this.$refs.medicineGrid.style.setProperty('--mouse-y', `${y}px`);
    },

    // 处理卡片悬停
    handleCardHover(index, event) {
      const card = event.currentTarget;
      card.classList.add('magnetic-active');
      
      // 添加重力场效果
      setTimeout(() => {
        card.style.transform = `translateY(-20px) rotateX(10deg) rotateY(5deg) scale(1.05)`;
        card.style.zIndex = '100';
      }, 100);
    },

    // 处理卡片离开
    handleCardLeave(index) {
      const cardRef = this.$refs[`medicineCard${index}`];
      if (cardRef && cardRef[0]) {
        const card = cardRef[0];
        card.classList.remove('magnetic-active');
        card.style.transform = '';
        card.style.zIndex = '';
      }
    },

    // 检查药品是否有权限
    isProductPermitted(productId) {
      return this.permittedMedicineIds.includes(productId);
    },
    
    // 加载权限药品列表
    async loadPermittedMedicines() {
      if (!isLoggedIn()) {
        this.permittedMedicineIds = [];
        return;
      }
      
      try {
        const token = getToken();
        const response = await getPermittedMedicines(token, 1, 100);
        
        if (response.code === 1 && response.data && response.data.data) {
          this.permittedMedicineIds = response.data.data.map(item => item.id);
        }
      } catch (error) {
        console.error('加载权限药品列表失败:', error);
      }
    },

    // 显示支付弹窗或跳转详情页
    async showPaymentDialog(product) {
      // 检查用户是否已登录
      if (!isLoggedIn()) {
        // 未登录，显示支付弹窗
        this.currentProduct = product;
        this.currentPrice = product.price;
        this.paymentDialogVisible = true;
        return;
      }
      
      // 已登录，检查是否有权限
      try {
        const token = getToken();
        const response = await getPermittedMedicines(token, 1, 100);
        
        if (response.code === 1 && response.data && response.data.data) {
          // 检查该药品是否在有权限列表中
          const permittedIds = response.data.data.map(item => item.id);
          
          if (permittedIds.includes(product.id)) {
            // 有权限，跳转到详情页
            this.$router.push({ name: 'ProductDetail', params: { id: product.id } });
          } else {
            // 没有权限，显示支付弹窗
            this.currentProduct = product;
            this.currentPrice = product.price;
            this.paymentDialogVisible = true;
          }
        } else {
          // API调用失败，显示支付弹窗
          this.currentProduct = product;
          this.currentPrice = product.price;
          this.paymentDialogVisible = true;
        }
      } catch (error) {
        console.error('检查权限失败:', error);
        // 错误时显示支付弹窗
        this.currentProduct = product;
        this.currentPrice = product.price;
        this.paymentDialogVisible = true;
      }
    },

    // 关闭支付弹窗
    closePaymentDialog() {
      this.paymentDialogVisible = false;
      this.currentProduct = {};
      this.currentPrice = 0;
    },

    // 神经网络节点样式生成
    getNeuralNodeStyle() {
      return {
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animationDelay: Math.random() * 5 + 's',
        '--neural-color': `hsl(${Math.random() * 60 + 200}, 70%, 60%)`,
        '--pulse-scale': 0.8 + Math.random() * 0.4
      }
    },

    // 量子粒子样式生成
    getQuantumParticleStyle() {
      return {
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animationDelay: Math.random() * 8 + 's',
        '--particle-size': Math.random() * 4 + 2 + 'px',
        '--float-amplitude': Math.random() * 50 + 20 + 'px'
      }
    },


    // 生成神经网络连接
    generateNeuralConnections() {
      const connections = [];
      const nodeCount = 20;
      
      for (let i = 0; i < nodeCount; i++) {
        const startX = Math.random() * 100;
        const startY = Math.random() * 100;
        const endX = Math.random() * 100;
        const endY = Math.random() * 100;
        
        // 生成曲线路径
        const controlX1 = startX + (Math.random() - 0.5) * 50;
        const controlY1 = startY + (Math.random() - 0.5) * 50;
        const controlX2 = endX + (Math.random() - 0.5) * 50;
        const controlY2 = endY + (Math.random() - 0.5) * 50;
        
        connections.push({
          path: `M${startX},${startY} C${controlX1},${controlY1} ${controlX2},${controlY2} ${endX},${endY}`,
          delay: Math.random() * 3
        });
      }
      
      this.neuralConnections = connections;
    },

    // 开始自动播放
    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, 5000);
    },

    // 停止自动播放
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },

    // 处理轮播图变化
    handleSlideChange(index) {
      this.currentSlide = index;
    },

    // 安全的HTML处理
    sanitizeHtml(html) {
      // 简单的HTML清理，移除潜在危险标签
      if (!html) return '';
      return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                 .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
                 .replace(/on\w+="[^"]*"/gi, '');
    },


    // 获取卡片样式
    getCardStyle(index) {
      const delay = index * 0.1;
      return {
        animationDelay: `${delay}s`
      };
    }
  },
  created() {
    this.loadBannerData();
    this.loadMedicineData();
    this.loadAboutUsData();
  },
  mounted() {
    // 加载轮播图数据
    this.loadBannerData();
    // 加载药品数据
    this.loadMedicineData();
    // 加载公司简介数据
    this.loadAboutUsData();
    // 加载权限药品列表
    this.loadPermittedMedicines();
    // 启动自动播放
    this.startAutoplay();
    // 生成神经网络连接
    this.generateNeuralConnections();
  },
  beforeUnmount() {
    // 清理定时器
    this.stopAutoplay();
  }
}
</script>

<style scoped>
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.pharma-home {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
}

/* 按钮样式 */
.primary-btn {
  background-color: #3a8ee6;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
}

.primary-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-2px);
}

.secondary-btn {
  background-color: white;
  color: #3a8ee6;
  border: 1px solid #3a8ee6;
  padding: 10px 25px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
}

.secondary-btn:hover {
  background-color: #f0f7ff;
}

.section-title {
  text-align: center;
  margin-bottom: 40px;
}

.highlight-text {
  background: linear-gradient(to right, #3a8ee6, #42b983);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  position: relative;
  display: inline-block;
  padding: 0 15px;
}

.section-title h2 {
  font-size: 2.2rem;
  color: #2c3e50;
  margin-bottom: 8px;
}

.section-title p {
  color: #666;
  font-size: 1.1rem;
}

/* 创新液态流动轮播图样式 */
.hero-section {
  position: relative;
  margin-top: 80px; /* 为顶部导航栏留出空间 */
  margin-bottom: 0;
  padding: 0;
  height: calc(100vh - 80px); /* 调整高度以适应导航栏间距 */
  overflow: hidden;
  background: radial-gradient(circle at 20% 80%, #0f0f23 0%, #1a1a3e 25%, #2d1b69 50%, #1a1a3e 75%, #0f0f23 100%);
  background-size: 400% 400%;
  animation: liquidGradient 15s ease-in-out infinite;
  perspective: 2000px;
  transform-style: preserve-3d;
}

/* 液态轮播图包装器 */
.liquid-carousel-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* 液态背景效果 */
.liquid-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
}

.liquid-blob {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.1));
  filter: blur(40px);
  animation: liquidFloat 8s ease-in-out infinite;
}

.liquid-blob:nth-child(1) {
  width: 300px;
  height: 300px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.liquid-blob:nth-child(2) {
  width: 400px;
  height: 400px;
  top: 60%;
  right: 10%;
  animation-delay: -2s;
}

.liquid-blob:nth-child(3) {
  width: 250px;
  height: 250px;
  bottom: 20%;
  left: 30%;
  animation-delay: -4s;
}

.liquid-blob:nth-child(4) {
  width: 350px;
  height: 350px;
  top: 30%;
  right: 30%;
  animation-delay: -6s;
}

.liquid-blob:nth-child(5) {
  width: 200px;
  height: 200px;
  bottom: 40%;
  right: 50%;
  animation-delay: -1s;
}

.liquid-blob:nth-child(6) {
  width: 450px;
  height: 450px;
  top: 50%;
  left: 50%;
  animation-delay: -3s;
}

/* 3D金字塔轮播容器 */
.pyramid-carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  perspective: 2000px;
  transform-style: preserve-3d;
}

.pyramid-carousel {
  position: relative;
  width: 90%;
  max-width: 1200px;
  height: 80%;
  transform-style: preserve-3d;
}

/* 3D金字塔幻灯片 */
.pyramid-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: all 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
  transform-style: preserve-3d;
  backface-visibility: hidden;
}

.pyramid-slide.active {
  z-index: 10;
}

.pyramid-slide.prev,
.pyramid-slide.next {
  z-index: 5;
}

.pyramid-slide.far {
  z-index: 1;
}

/* 立体卡片样式 */
.slide-card {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 30px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  box-shadow: 
    0 50px 100px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transform-style: preserve-3d;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

/* 图片层样式 */
.slide-image-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 30px;
  z-index: 1;
}

.slide-image-layer img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
  filter: brightness(0.8) contrast(1.2) saturate(1.3);
  transform: scale(1.1);
}

.pyramid-slide.active .slide-image-layer img {
  filter: brightness(0.9) contrast(1.1) saturate(1.2);
  transform: scale(1);
}

/* 图片涟漪效果 */
.image-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: rippleExpand 3s ease-out infinite;
}

/* 液态遮罩层 */
.liquid-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  z-index: 2;
  overflow: hidden;
}

.liquid-svg {
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100%;
}

.liquid-path {
  fill: url(#liquidGradient);
  animation: liquidWave 4s ease-in-out infinite;
}

/* 内容层样式 */
.slide-content-layer {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  z-index: 5;
  display: flex;
  align-items: flex-end;
  padding: 40px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.3) 30%,
    rgba(0, 0, 0, 0.8) 100%
  );
  border-radius: 0 0 30px 30px;
}

.content-container {
  width: 100%;
  color: white;
}


.slide-title {
  font-size: 3rem;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.1;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  background: linear-gradient(135deg, #ffffff, #f8fafc, #e2e8f0);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: -0.02em;
}

.slide-description {
  font-size: 1.4rem;
  line-height: 1.6;
  margin-bottom: 30px;
  opacity: 0.9;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  font-weight: 400;
}

/* 液态按钮样式 */
.slide-actions {
  margin-top: 30px;
}

.liquid-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 15px;
  padding: 18px 40px;
  background: transparent;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  color: white;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  overflow: hidden;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.liquid-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s ease;
}

.liquid-button:hover::before {
  left: 100%;
}

.button-liquid {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 100%;
  background: linear-gradient(135deg, #60a5fa, #8b5cf6, #ec4899);
  border-radius: 50px;
  transition: width 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  z-index: -1;
}

.liquid-button:hover .button-liquid {
  width: 100%;
}

.liquid-button:hover {
  transform: translateY(-5px) scale(1.05);
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 20px 60px rgba(96, 165, 250, 0.4);
}

.button-text {
  position: relative;
  z-index: 2;
}

.button-arrow {
  transition: transform 0.3s ease;
  position: relative;
  z-index: 2;
}

.liquid-button:hover .button-arrow {
  transform: translateX(5px);
}

/* 装饰性粒子 */
.slide-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

.particle {
  position: absolute;
  border-radius: 50%;
  animation: particleFloat 6s ease-in-out infinite;
  opacity: 0.7;
}

/* 控制界面样式 */
.control-interface {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 20;
  pointer-events: none;
}

/* 侧边导航 */
.side-navigation {
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  pointer-events: auto;
}

.nav-dot {
  position: relative;
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot-core {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.dot-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.dot-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border: 2px solid rgba(96, 165, 250, 0.6);
  border-radius: 50%;
  opacity: 0;
  animation: dotPulse 2s ease-in-out infinite;
}

.nav-dot.active .dot-core {
  background: linear-gradient(135deg, #60a5fa, #8b5cf6);
  width: 16px;
  height: 16px;
  box-shadow: 0 0 20px rgba(96, 165, 250, 0.6);
}

.nav-dot.active .dot-ring {
  border-color: rgba(96, 165, 250, 0.8);
  transform: translate(-50%, -50%) scale(1.2);
}

.nav-dot.active .dot-pulse {
  opacity: 1;
}

/* 底部进度条 */
.progress-bar {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 20px;
  pointer-events: auto;
}

.progress-track {
  width: 300px;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #60a5fa, #8b5cf6, #ec4899);
  border-radius: 3px;
  transition: width 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: 0 0 20px rgba(96, 165, 250, 0.6);
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5));
  animation: progressShine 2s ease-in-out infinite;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

.current-index {
  color: #60a5fa;
  font-size: 1.3rem;
}

.separator {
  opacity: 0.7;
}

.total-count {
  opacity: 0.8;
}

/* 3D控制按钮 */
.control-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 80px;
  background: transparent;
  border: none;
  cursor: pointer;
  pointer-events: auto;
  perspective: 200px;
  transform-style: preserve-3d;
}

.control-prev {
  left: 40px;
}

.control-next {
  right: 40px;
}

.button-face {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transform: translateZ(0);
}

.button-shadow {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  filter: blur(15px);
  transform: translateZ(-20px);
  transition: all 0.4s ease;
}

.control-button:hover .button-face {
  background: rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.6);
  transform: translateZ(20px) rotateY(10deg);
  box-shadow: 0 20px 60px rgba(96, 165, 250, 0.4);
}

.control-button:hover .button-shadow {
  transform: translateZ(-30px) scale(1.2);
  opacity: 0.6;
}

/* 环境光效 */
.ambient-lights {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.light-beam {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  animation: lightFloat 12s ease-in-out infinite;
}

.light-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.light-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%);
  top: 60%;
  right: 10%;
  animation-delay: -4s;
}

.light-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%);
  bottom: 20%;
  left: 50%;
  animation-delay: -8s;
}

/* 液态加载效果 */
.liquid-loading {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
}

.liquid-wave {
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #60a5fa, #8b5cf6, #ec4899);
  border-radius: 50%;
  animation: liquidWaveLoading 1.5s ease-in-out infinite;
}

.liquid-wave:nth-child(2) {
  animation-delay: 0.2s;
}

.liquid-wave:nth-child(3) {
  animation-delay: 0.4s;
}

.loading-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  font-weight: 600;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  animation: textPulse 2s ease-in-out infinite;
}

/* 错误动画 */
.error-animation {
  margin-bottom: 20px;
  animation: errorShake 0.8s ease-in-out;
}

.error-animation svg {
  color: #ef4444;
  filter: drop-shadow(0 4px 20px rgba(239, 68, 68, 0.4));
}

/* 创新动画关键帧 */
@keyframes liquidGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
}

@keyframes liquidFloat {
  0%, 100% {
    transform: translateY(0px) translateX(0px) scale(1);
  }
  25% {
    transform: translateY(-20px) translateX(10px) scale(1.1);
  }
  50% {
    transform: translateY(0px) translateX(20px) scale(0.9);
  }
  75% {
    transform: translateY(20px) translateX(-10px) scale(1.05);
  }
}

@keyframes rippleExpand {
  0% {
    width: 0;
    height: 0;
    opacity: 1;
  }
  50% {
    width: 200px;
    height: 200px;
    opacity: 0.5;
  }
  100% {
    width: 400px;
    height: 400px;
    opacity: 0;
  }
}

@keyframes liquidWave {
  0% {
    d: path('M0,100 Q50,50 100,100 T200,100 L200,200 L0,200 Z');
  }
  25% {
    d: path('M0,100 Q50,150 100,100 T200,100 L200,200 L0,200 Z');
  }
  50% {
    d: path('M0,100 Q50,50 100,100 T200,100 L200,200 L0,200 Z');
  }
  75% {
    d: path('M0,100 Q50,150 100,100 T200,100 L200,200 L0,200 Z');
  }
  100% {
    d: path('M0,100 Q50,50 100,100 T200,100 L200,200 L0,200 Z');
  }
}

@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes particleFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.7;
  }
  25% {
    transform: translateY(-10px) rotate(90deg);
    opacity: 1;
  }
  50% {
    transform: translateY(0px) rotate(180deg);
    opacity: 0.5;
  }
  75% {
    transform: translateY(5px) rotate(270deg);
    opacity: 0.8;
  }
}

@keyframes dotPulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0.2;
  }
}

@keyframes progressShine {
  0% {
    transform: translateX(-20px);
  }
  100% {
    transform: translateX(20px);
  }
}

@keyframes lightFloat {
  0%, 100% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0.1;
  }
  33% {
    transform: translateY(-30px) translateX(20px) rotate(120deg);
    opacity: 0.2;
  }
  66% {
    transform: translateY(0px) translateX(-20px) rotate(240deg);
    opacity: 0.15;
  }
}

@keyframes liquidWaveLoading {
  0%, 100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  50% {
    transform: scale(1.5) translateY(-20px);
    opacity: 0.7;
  }
}

@keyframes textPulse {
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

/* 新药品展示区域动画关键帧 */
@keyframes quantumGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
}

@keyframes gridScan {
  0% {
    opacity: 0;
    transform: scaleX(0);
  }
  50% {
    opacity: 1;
    transform: scaleX(1);
  }
  100% {
    opacity: 0;
    transform: scaleX(0);
  }
}

@keyframes holoFloat {
  0%, 100% {
    transform: translateY(0px) translateX(0px) scale(1);
    opacity: 0.7;
  }
  25% {
    transform: translateY(-15px) translateX(10px) scale(1.1);
    opacity: 1;
  }
  50% {
    transform: translateY(0px) translateX(20px) scale(0.9);
    opacity: 0.5;
  }
  75% {
    transform: translateY(15px) translateX(-10px) scale(1.05);
    opacity: 0.8;
  }
}

@keyframes quantumPulse {
  0%, 100% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.2);
  }
}

@keyframes titleFloat {
  0%, 100% {
    transform: translateY(0px) rotateX(0deg);
  }
  50% {
    transform: translateY(-5px) rotateX(2deg);
  }
}

@keyframes fieldPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes cardFloat {
  0%, 100% {
    transform: translateY(0px) rotateX(0deg) rotateY(0deg);
  }
  25% {
    transform: translateY(var(--float-range, -8px)) rotateX(2deg) rotateY(var(--rotation-range, 1deg));
  }
  50% {
    transform: translateY(0px) rotateX(0deg) rotateY(var(--rotation-range, -1deg));
  }
  75% {
    transform: translateY(calc(var(--float-range, 4px) / 2)) rotateX(-1deg) rotateY(var(--rotation-range, 0.5deg));
  }
}

@keyframes fieldLine1 {
  0%, 100% { opacity: 0; transform: scaleX(0); }
  50% { opacity: 1; transform: scaleX(1); }
}

@keyframes fieldLine2 {
  0%, 100% { opacity: 0; transform: scaleY(0); }
  50% { opacity: 1; transform: scaleY(1); }
}

@keyframes fieldLine3 {
  0%, 100% { opacity: 0; transform: rotate(45deg) scale(0); }
  50% { opacity: 1; transform: rotate(45deg) scale(1); }
}

@keyframes fieldLine4 {
  0%, 100% { opacity: 0; transform: rotate(-45deg) scale(0); }
  50% { opacity: 1; transform: rotate(-45deg) scale(1); }
}

@keyframes fieldLine5 {
  0%, 100% { opacity: 0; transform: scaleX(0); }
  50% { opacity: 0.5; transform: scaleX(1); }
}

@keyframes fieldLine6 {
  0%, 100% { opacity: 0; transform: scaleX(0); }
  50% { opacity: 0.5; transform: scaleX(1); }
}

/* 新增动画关键帧 */
@keyframes borderScan1 {
  0% { opacity: 0; transform: translateX(-100%); }
  50% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(100%); }
}

@keyframes borderScan2 {
  0% { opacity: 0; transform: translateY(-100%); }
  50% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(100%); }
}

@keyframes borderScan3 {
  0% { opacity: 0; transform: translateX(100%); }
  50% { opacity: 1; transform: translateX(0); }
  100% { opacity: 0; transform: translateX(-100%); }
}

@keyframes borderScan4 {
  0% { opacity: 0; transform: translateY(100%); }
  50% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-100%); }
}

@keyframes portalPulse {
  0%, 100% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.1);
  }
}

@keyframes tagGlow {
  0% { left: -100%; }
  100% { left: 100%; }
}

@keyframes nameGlow {
  0% { left: -100%; }
  100% { left: 100%; }
}

@keyframes streamFlow {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0;
  }
}

@keyframes teleportField {
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes distortionRing {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2) rotate(180deg);
    opacity: 0.1;
  }
}

@keyframes massPulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 0 30px rgba(0, 212, 255, 0.8);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    box-shadow: 0 0 50px rgba(0, 212, 255, 1);
  }
}

@keyframes orbit {
  from {
    transform: rotate(0deg) translateX(var(--orbit-radius, 50px)) rotate(0deg);
  }
  to {
    transform: rotate(360deg) translateX(var(--orbit-radius, 50px)) rotate(-360deg);
  }
}

@keyframes errorGlitch {
  0%, 100% {
    transform: translateX(0);
    filter: drop-shadow(0 0 20px rgba(255, 68, 68, 0.6));
  }
  25% {
    transform: translateX(-2px);
    filter: drop-shadow(0 0 25px rgba(255, 68, 68, 0.8));
  }
  75% {
    transform: translateX(2px);
    filter: drop-shadow(0 0 15px rgba(255, 68, 68, 0.4));
  }
}

@keyframes voidRipple {
  0% {
    width: 0;
    height: 0;
    opacity: 1;
  }
  100% {
    width: 100px;
    height: 100px;
    opacity: 0;
  }
}

/* 液态轮播图响应式设计 */
@media (min-width: 1400px) {
  .hero-section {
    margin-top: 90px; /* 大屏幕可能有更高的导航栏 */
    height: calc(100vh - 90px);
  }
}

@media (max-width: 1024px) {
  .pyramid-carousel {
    width: 95%;
    height: 75%;
  }
  
  .slide-title {
    font-size: 2.5rem;
  }
  
  .slide-description {
    font-size: 1.2rem;
  }
  
  .side-navigation {
    right: 30px;
    gap: 15px;
  }
  
  .nav-dot {
    width: 16px;
    height: 16px;
  }
  
  .dot-core {
    width: 10px;
    height: 10px;
  }
  
  .control-button {
    width: 70px;
    height: 70px;
  }
  
  .control-prev {
    left: 30px;
  }
  
  .control-next {
    right: 30px;
  }
  
  .progress-track {
    width: 250px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    margin-top: 70px; /* 移动端导航栏可能较小 */
    height: calc(80vh - 70px);
  }
  
  .pyramid-carousel {
    width: 95%;
    height: 70%;
  }
  
  .slide-content-layer {
    padding: 25px;
  }
  
  .slide-title {
    font-size: 2rem;
    margin-bottom: 15px;
  }
  
  .slide-description {
    font-size: 1rem;
    margin-bottom: 20px;
  }
  
  .liquid-button {
    padding: 14px 30px;
    font-size: 1rem;
    gap: 10px;
  }
  
  .side-navigation {
    right: 20px;
    gap: 12px;
  }
  
  .nav-dot {
    width: 14px;
    height: 14px;
  }
  
  .dot-core {
    width: 8px;
    height: 8px;
  }
  
  .control-button {
    width: 60px;
    height: 60px;
  }
  
  .control-prev {
    left: 20px;
  }
  
  .control-next {
    right: 20px;
  }
  
  .progress-bar {
    bottom: 30px;
    gap: 15px;
  }
  
  .progress-track {
    width: 200px;
    height: 4px;
  }
  
  .progress-info {
    font-size: 1rem;
  }
  
  .current-index {
    font-size: 1.1rem;
  }
  
  .liquid-blob {
    filter: blur(20px);
  }
  
  .liquid-blob:nth-child(1) {
    width: 200px;
    height: 200px;
  }
  
  .liquid-blob:nth-child(2) {
    width: 250px;
    height: 250px;
  }
  
  .liquid-blob:nth-child(3) {
    width: 150px;
    height: 150px;
  }
  
  .liquid-blob:nth-child(4) {
    width: 220px;
    height: 220px;
  }
  
  .liquid-blob:nth-child(5) {
    width: 120px;
    height: 120px;
  }
  
  .liquid-blob:nth-child(6) {
    width: 280px;
    height: 280px;
  }
}

@media (max-width: 480px) {
  .hero-section {
    margin-top: 60px; /* 小屏幕进一步优化间距 */
    height: calc(70vh - 60px);
  }
  
  .pyramid-carousel {
    width: 100%;
    height: 65%;
  }
  
  .slide-content-layer {
    padding: 20px;
    height: 70%;
  }
  
  
  .slide-title {
    font-size: 1.6rem;
    margin-bottom: 12px;
  }
  
  .slide-description {
    font-size: 0.9rem;
    margin-bottom: 15px;
  }
  
  .liquid-button {
    padding: 12px 25px;
    font-size: 0.9rem;
  }
  
  .side-navigation {
    right: 15px;
    gap: 10px;
  }
  
  .nav-dot {
    width: 12px;
    height: 12px;
  }
  
  .control-button {
    width: 50px;
    height: 50px;
  }
  
  .control-prev {
    left: 15px;
  }
  
  .control-next {
    right: 15px;
  }
  
  .progress-bar {
    bottom: 20px;
    flex-direction: column;
    gap: 10px;
  }
  
  .progress-track {
    width: 150px;
  }
  
  .light-beam {
    filter: blur(30px);
  }
}

/* 现代化动画效果 */
@keyframes gradientShift {
  0% {
    background-position: 0% 0%;
  }
  12.5% {
    background-position: 25% 25%;
  }
  25% {
    background-position: 50% 50%;
  }
  37.5% {
    background-position: 75% 75%;
  }
  50% {
    background-position: 100% 100%;
  }
  62.5% {
    background-position: 75% 25%;
  }
  75% {
    background-position: 50% 0%;
  }
  87.5% {
    background-position: 25% 75%;
  }
  100% {
    background-position: 0% 0%;
  }
}

@keyframes slideInFromLeft {
  0% {
    transform: translateX(-150px) rotateY(45deg) scale(0.8);
    opacity: 0;
    filter: blur(10px) brightness(0.5);
  }
  30% {
    transform: translateX(-50px) rotateY(20deg) scale(0.9);
    opacity: 0.5;
    filter: blur(5px) brightness(0.7);
  }
  60% {
    transform: translateX(-10px) rotateY(5deg) scale(0.95);
    opacity: 0.8;
    filter: blur(2px) brightness(0.9);
  }
  100% {
    transform: translateX(0) rotateY(0deg) scale(1);
    opacity: 1;
    filter: blur(0px) brightness(1);
  }
}

@keyframes textGlow {
  0% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(59, 130, 246, 0.2), 0 0 60px rgba(139, 92, 246, 0.1);
    transform: scale(1);
  }
  25% {
    text-shadow: 0 0 30px rgba(255, 255, 255, 0.5), 0 0 60px rgba(139, 92, 246, 0.4), 0 0 90px rgba(236, 72, 153, 0.3);
    transform: scale(1.02);
  }
  50% {
    text-shadow: 0 0 40px rgba(255, 255, 255, 0.7), 0 0 80px rgba(236, 72, 153, 0.6), 0 0 120px rgba(251, 191, 36, 0.4);
    transform: scale(1.05);
  }
  75% {
    text-shadow: 0 0 35px rgba(255, 255, 255, 0.6), 0 0 70px rgba(251, 191, 36, 0.5), 0 0 105px rgba(34, 197, 94, 0.3);
    transform: scale(1.03);
  }
  100% {
    text-shadow: 0 0 25px rgba(255, 255, 255, 0.4), 0 0 50px rgba(34, 197, 94, 0.3), 0 0 75px rgba(59, 130, 246, 0.2);
    transform: scale(1);
  }
}

@keyframes buttonPulse {
  0%, 100% {
    box-shadow: 0 15px 35px rgba(59, 130, 246, 0.4), 0 0 0 0 rgba(59, 130, 246, 0.7), inset 0 0 20px rgba(255, 255, 255, 0.1);
    transform: scale(1) rotateZ(0deg);
  }
  25% {
    box-shadow: 0 18px 40px rgba(139, 92, 246, 0.5), 0 0 0 5px rgba(139, 92, 246, 0.3), inset 0 0 25px rgba(255, 255, 255, 0.15);
    transform: scale(1.02) rotateZ(1deg);
  }
  50% {
    box-shadow: 0 20px 45px rgba(236, 72, 153, 0.6), 0 0 0 10px rgba(236, 72, 153, 0), inset 0 0 30px rgba(255, 255, 255, 0.2);
    transform: scale(1.05) rotateZ(0deg);
  }
  75% {
    box-shadow: 0 18px 40px rgba(251, 191, 36, 0.5), 0 0 0 5px rgba(251, 191, 36, 0.3), inset 0 0 25px rgba(255, 255, 255, 0.15);
    transform: scale(1.02) rotateZ(-1deg);
  }
}

@keyframes slideOutScale {
  0% {
    transform: scale(1) translateX(0) rotateY(0deg);
    opacity: 1;
    filter: brightness(0.9) blur(0px) hue-rotate(0deg);
  }
  25% {
    transform: scale(0.9) translateX(-25%) rotateY(15deg);
    opacity: 0.8;
    filter: brightness(0.7) blur(2px) hue-rotate(90deg);
  }
  50% {
    transform: scale(0.7) translateX(-50%) rotateY(45deg);
    opacity: 0.5;
    filter: brightness(0.5) blur(5px) hue-rotate(180deg);
  }
  75% {
    transform: scale(0.4) translateX(-75%) rotateY(75deg);
    opacity: 0.2;
    filter: brightness(0.3) blur(8px) hue-rotate(270deg);
  }
  100% {
    transform: scale(0.1) translateX(-100%) rotateY(90deg);
    opacity: 0;
    filter: brightness(0.1) blur(15px) hue-rotate(360deg);
  }
}

@keyframes slideInScale {
  0% {
    transform: scale(0.1) translateX(100%) rotateY(-90deg);
    opacity: 0;
    filter: brightness(0.1) blur(15px) hue-rotate(-360deg);
  }
  25% {
    transform: scale(0.4) translateX(75%) rotateY(-75deg);
    opacity: 0.2;
    filter: brightness(0.3) blur(8px) hue-rotate(-270deg);
  }
  50% {
    transform: scale(0.7) translateX(50%) rotateY(-45deg);
    opacity: 0.5;
    filter: brightness(0.5) blur(5px) hue-rotate(-180deg);
  }
  75% {
    transform: scale(0.9) translateX(25%) rotateY(-15deg);
    opacity: 0.8;
    filter: brightness(0.7) blur(2px) hue-rotate(-90deg);
  }
  100% {
    transform: scale(1) translateX(0) rotateY(0deg);
    opacity: 1;
    filter: brightness(0.9) blur(0px) hue-rotate(0deg);
  }
}

@keyframes rotateOut {
  0% {
    transform: rotateY(0deg) scale(1);
    opacity: 1;
  }
  100% {
    transform: rotateY(-90deg) scale(0.8);
    opacity: 0;
  }
}

@keyframes rotateIn {
  0% {
    transform: rotateY(90deg) scale(0.8);
    opacity: 0;
  }
  100% {
    transform: rotateY(0deg) scale(1);
    opacity: 1;
  }
}

/* 为每个碎片设置不同的随机位移 */
.fragment:nth-child(1) { --random-x: -300px; --random-y: -200px; }
.fragment:nth-child(2) { --random-x: -150px; --random-y: -250px; }
.fragment:nth-child(3) { --random-x: 0px; --random-y: -300px; }
.fragment:nth-child(4) { --random-x: 150px; --random-y: -250px; }
.fragment:nth-child(5) { --random-x: 300px; --random-y: -200px; }
.fragment:nth-child(6) { --random-x: -250px; --random-y: -100px; }
.fragment:nth-child(7) { --random-x: -100px; --random-y: -150px; }
.fragment:nth-child(8) { --random-x: 0px; --random-y: -100px; }
.fragment:nth-child(9) { --random-x: 100px; --random-y: -150px; }
.fragment:nth-child(10) { --random-x: 250px; --random-y: -100px; }
.fragment:nth-child(11) { --random-x: -300px; --random-y: 0px; }
.fragment:nth-child(12) { --random-x: -150px; --random-y: 0px; }
.fragment:nth-child(13) { --random-x: 0px; --random-y: 0px; }
.fragment:nth-child(14) { --random-x: 150px; --random-y: 0px; }
.fragment:nth-child(15) { --random-x: 300px; --random-y: 0px; }
.fragment:nth-child(16) { --random-x: -250px; --random-y: 100px; }
.fragment:nth-child(17) { --random-x: -100px; --random-y: 150px; }
.fragment:nth-child(18) { --random-x: 0px; --random-y: 100px; }
.fragment:nth-child(19) { --random-x: 100px; --random-y: 150px; }
.fragment:nth-child(20) { --random-x: 250px; --random-y: 100px; }
.fragment:nth-child(21) { --random-x: -300px; --random-y: 200px; }
.fragment:nth-child(22) { --random-x: -150px; --random-y: 250px; }
.fragment:nth-child(23) { --random-x: 0px; --random-y: 300px; }
.fragment:nth-child(24) { --random-x: 150px; --random-y: 250px; }
.fragment:nth-child(25) { --random-x: 300px; --random-y: 200px; }

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.banner-content {
  text-align: center;
  color: white;
  max-width: 600px;
  padding: 0 20px;
}

.banner-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.banner-description {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 30px;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.banner-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.action-btn {
  background: linear-gradient(45deg, #3a8ee6, #42b983);
  color: white;
  padding: 12px 30px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(58, 142, 230, 0.3);
  border: 2px solid transparent;
}

.action-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(58, 142, 230, 0.4);
  background: linear-gradient(45deg, #42b983, #3a8ee6);
}





/* 现代化轮播图控制样式 */
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 30;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.carousel-arrow:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-50%) scale(1.15);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.4);
}

.carousel-arrow svg {
  width: 28px;
  height: 28px;
}

.carousel-arrow-left {
  left: 3rem;
}

.carousel-arrow-right {
  right: 3rem;
}

.carousel-indicators {
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
  z-index: 30;
}

.indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.indicator::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.6);
  transform: scale(1.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.indicator:hover::before {
  opacity: 0.3;
}

.indicator.active {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-color: rgba(255, 255, 255, 0.8);
  transform: scale(1.4);
  box-shadow: 0 0 25px rgba(59, 130, 246, 0.6);
}

.indicator.active::before {
  opacity: 1;
}

/* 现代化轮播图响应式设计 */
@media (max-width: 1024px) {
  .hero-section {
    height: 80vh;
  }

  .banner-title {
    font-size: 3.5rem;
  }

  .banner-description {
    font-size: 1.5rem;
  }

  .banner-content {
    padding: 0 3rem;
  }

  .carousel-arrow {
    width: 60px;
    height: 60px;
  }

  .carousel-arrow svg {
    width: 24px;
    height: 24px;
  }

  .carousel-arrow-left {
    left: 2rem;
  }

  .carousel-arrow-right {
    right: 2rem;
  }

  .indicator {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 70vh;
  }

  .banner-title {
    font-size: 2.8rem;
    margin-bottom: 1.5rem;
  }

  .banner-description {
    font-size: 1.3rem;
    margin-bottom: 2rem;
  }

  .banner-content {
    padding: 0 2rem;
  }

  .banner-actions {
    flex-direction: column;
    gap: 1.5rem;
  }

  .action-btn {
    padding: 1rem 2.5rem;
    font-size: 1rem;
  }

  .carousel-arrow {
    width: 50px;
    height: 50px;
  }

  .carousel-arrow svg {
    width: 20px;
    height: 20px;
  }

  .carousel-arrow-left {
    left: 1.5rem;
  }

  .carousel-arrow-right {
    right: 1.5rem;
  }

  .carousel-indicators {
    bottom: 2rem;
  }

  .indicator {
    width: 12px;
    height: 12px;
  }

  /* 移动端碎片网格调整 */
  .image-fragments {
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(5, 1fr);
  }
}

/* 重力悬浮药品展示区域 - 未来科技设计 */
.medicine-showcase {
  padding: 150px 20px 120px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg,
    #0a0a0f 0%,
    #151520 25%,
    #1a1a2e 50%,
    #16213e 75%,
    #0f3460 100%);
  background-size: 400% 400%;
  animation: quantumGradient 25s ease infinite;
  min-height: 100vh;
}

/* 全息投影背景 */
.holographic-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.holo-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
}

.grid-line {
  position: absolute;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  height: 1px;
  width: 100%;
  animation: gridScan 8s linear infinite;
}

.grid-line:nth-child(odd) {
  top: calc(var(--line-index, 0) * 5%);
  animation-delay: calc(var(--line-index, 0) * 0.1s);
}

.grid-line:nth-child(even) {
  transform: rotate(90deg);
  transform-origin: center;
  left: calc(var(--line-index, 0) * 5%);
  animation-delay: calc(var(--line-index, 0) * 0.15s);
}

.holo-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.holo-particle {
  position: absolute;
  border-radius: 50%;
  animation: holoFloat 6s ease-in-out infinite;
}

.quantum-field {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.quantum-wave {
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, rgba(0, 212, 255, 0.1) 0%, transparent 70%);
  animation: quantumPulse 4s ease-in-out infinite;
}

.quantum-wave:nth-child(2) { animation-delay: 1s; }
.quantum-wave:nth-child(3) { animation-delay: 2s; }
.quantum-wave:nth-child(4) { animation-delay: 3s; }
.quantum-wave:nth-child(5) { animation-delay: 4s; }

/* 磁性标题区域 */
.showcase-header {
  position: relative;
  z-index: 10;
  margin-bottom: 80px;
}

.magnetic-title-container {
  display: flex;
  justify-content: center;
  padding: 0 20px;
}

.title-field {
  position: relative;
  text-align: center;
  background: rgba(0, 212, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  padding: 60px 80px;
  border: 2px solid rgba(0, 212, 255, 0.2);
  box-shadow: 0 0 60px rgba(0, 212, 255, 0.3);
}

.field-distortion {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(0, 212, 255, 0.1), transparent);
  animation: rotate 20s linear infinite;
  pointer-events: none;
}

.gravity-title {
  font-size: 4rem;
  font-weight: 900;
  margin-bottom: 25px;
  position: relative;
  z-index: 2;
}

.title-fragment {
  display: inline-block;
  background: linear-gradient(135deg, #00d4ff, #ff00d4, #d4ff00, #ff4900);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: titleFloat 3s ease-in-out infinite, gradientShift 4s ease-in-out infinite;
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
}

.subtitle-hologram {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.3rem;
  margin-bottom: 40px;
  font-weight: 500;
  position: relative;
  z-index: 2;
}

.quantum-btn {
  position: relative;
  display: inline-block;
  text-decoration: none;
  z-index: 2;
}

.btn-core {
  position: relative;
  background: rgba(0, 212, 255, 0.1);
  border: 2px solid rgba(0, 212, 255, 0.5);
  border-radius: 50px;
  padding: 18px 40px;
  color: #00d4ff;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.6s ease;
  backdrop-filter: blur(15px);
  overflow: hidden;
}

.btn-energy {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  transition: left 0.8s ease;
}

.quantum-btn:hover .btn-energy {
  left: 100%;
}

.quantum-btn:hover .btn-core {
  background: rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 212, 255, 0.4);
}

.btn-field {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50px;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.quantum-btn:hover .btn-field {
  opacity: 1;
  animation: fieldPulse 1s ease-in-out infinite;
}

.btn-arrow {
  margin-left: 10px;
  transition: transform 0.3s ease;
}

.quantum-btn:hover .btn-arrow {
  transform: translateX(5px);
}

/* 重力悬浮网格 */
.gravity-medicine-grid {
  position: relative;
  z-index: 10;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  padding: 40px 20px;
  perspective: 2000px;
  transform-style: preserve-3d;
}

.floating-medicine-card {
  position: relative;
  animation: cardFloat 6s ease-in-out infinite;
  transform-style: preserve-3d;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  cursor: pointer;
}

.floating-medicine-card.magnetic-active {
  animation-play-state: paused;
}

/* 重力场可视化 */
.gravity-field-viz {
  position: absolute;
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.floating-medicine-card:hover .gravity-field-viz {
  opacity: 1;
}

.field-line {
  position: absolute;
  background: linear-gradient(45deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  border-radius: 2px;
}

.field-line:nth-child(1) {
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  animation: fieldLine1 2s ease-in-out infinite;
}

.field-line:nth-child(2) {
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  animation: fieldLine2 2s ease-in-out infinite 0.3s;
}

.field-line:nth-child(3) {
  top: 0;
  left: 0;
  right: 100%;
  bottom: 100%;
  width: 2px;
  transform: rotate(45deg);
  transform-origin: top left;
  animation: fieldLine3 2s ease-in-out infinite 0.6s;
}

.field-line:nth-child(4) {
  top: 0;
  right: 0;
  left: 100%;
  bottom: 100%;
  width: 2px;
  transform: rotate(-45deg);
  transform-origin: top right;
  animation: fieldLine4 2s ease-in-out infinite 0.9s;
}

.field-line:nth-child(5) {
  top: 25%;
  left: 0;
  right: 0;
  height: 1px;
  animation: fieldLine5 2s ease-in-out infinite 1.2s;
}

.field-line:nth-child(6) {
  top: 75%;
  left: 0;
  right: 0;
  height: 1px;
  animation: fieldLine6 2s ease-in-out infinite 1.5s;
}

/* 悬浮卡片容器 */
.card-hull {
  position: relative;
  width: 100%;
  height: 450px;
  background: rgba(10, 15, 30, 0.95);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-radius: 25px;
  overflow: hidden;
  border: 2px solid rgba(0, 212, 255, 0.3);
  box-shadow: 
    0 25px 60px rgba(0, 212, 255, 0.2),
    0 0 0 1px rgba(0, 212, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  transform-style: preserve-3d;
  z-index: 5;
  cursor: pointer;
  pointer-events: auto;
}

.floating-medicine-card:hover .card-hull {
  border-color: rgba(0, 212, 255, 0.6);
  box-shadow: 
    0 40px 100px rgba(0, 212, 255, 0.4),
    0 0 0 1px rgba(0, 212, 255, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  background: rgba(10, 15, 30, 0.98);
}

/* 全息边框 */
.holo-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
}

.border-segment {
  position: absolute;
  background: linear-gradient(45deg, transparent, #00d4ff, transparent);
  opacity: 0;
  transition: opacity 0.6s ease;
}

.border-segment:nth-child(1) {
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  animation: borderScan1 3s ease-in-out infinite;
}

.border-segment:nth-child(2) {
  top: 0;
  bottom: 0;
  right: 0;
  width: 2px;
  animation: borderScan2 3s ease-in-out infinite 0.75s;
}

.border-segment:nth-child(3) {
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  animation: borderScan3 3s ease-in-out infinite 1.5s;
}

.border-segment:nth-child(4) {
  top: 0;
  bottom: 0;
  left: 0;
  width: 2px;
  animation: borderScan4 3s ease-in-out infinite 2.25s;
}

.floating-medicine-card:hover .border-segment {
  opacity: 1;
}

/* 药品图像层 */
.medicine-image-layer {
  position: relative;
  height: 250px;
  overflow: hidden;
  border-radius: 25px 25px 0 0;
  z-index: 2;
  pointer-events: auto;
}

.image-portal {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.image-portal img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  filter: brightness(0.8) contrast(1.2) saturate(1.1);
}

.floating-medicine-card:hover .image-portal img {
  transform: scale(1.1) rotate(2deg);
  filter: brightness(1) contrast(1.3) saturate(1.3);
}

.portal-energy {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(0, 212, 255, 0.2) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.6s ease;
  animation: portalPulse 2s ease-in-out infinite;
}

.floating-medicine-card:hover .portal-energy {
  opacity: 1;
}

.category-tag {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 212, 255, 0.9);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  color: white;
  z-index: 10;
  box-shadow: 0 8px 25px rgba(0, 212, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.tag-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: tagGlow 2s ease-in-out infinite;
}

/* 已购买标签 */
.purchased-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  backdrop-filter: blur(10px);
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  color: white;
  z-index: 11;
  box-shadow: 0 8px 25px rgba(82, 196, 26, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  gap: 4px;
  animation: purchasedPulse 2s ease-in-out infinite;
}

@keyframes purchasedPulse {
  0%, 100% {
    box-shadow: 0 8px 25px rgba(82, 196, 26, 0.4);
  }
  50% {
    box-shadow: 0 8px 35px rgba(82, 196, 26, 0.6);
  }
}

/* 信息投影层 */
.info-projection {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 300px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(10, 15, 30, 0.9) 15%,
    rgba(10, 15, 30, 0.98) 100%
  );
  z-index: 5;
  border-radius: 0 0 25px 25px;
  pointer-events: auto;
}

.projection-container {
  padding: 15px 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.medicine-name {
  font-size: 1.3rem;
  font-weight: 800;
  color: #ffffff;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.8), 0 2px 4px rgba(0, 0, 0, 0.5);
  margin-bottom: 10px;
  position: relative;
}

.name-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  animation: nameGlow 3s ease-in-out infinite;
  pointer-events: none;
}

/* 数据流 */
.data-streams {
  margin-bottom: 15px;
}

.data-stream {
  position: relative;
  margin-bottom: 6px;
  padding: 4px 0;
  border-left: 2px solid rgba(0, 212, 255, 0.3);
  padding-left: 12px;
}

.stream-label {
  font-size: 0.8rem;
  color: rgba(0, 212, 255, 0.9);
  font-weight: 600;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.stream-value {
  color: #ffffff;
  font-size: 0.9rem;
  line-height: 1.4;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.description-stream .stream-value {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.price-value {
  color: #FF6B6B;
  font-size: 1.3rem;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(255, 107, 107, 0.5);
}

.stream-flow {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    #00d4ff 50%,
    transparent 100%
  );
  animation: streamFlow 2s ease-in-out infinite;
  opacity: 0;
}

.floating-medicine-card:hover .stream-flow {
  opacity: 1;
}

/* 量子传送按钮 */
.teleport-zone {
  display: flex;
  justify-content: flex-end;
}

.teleport-btn {
  position: relative;
  text-decoration: none;
  display: inline-block;
  pointer-events: auto;
  cursor: pointer;
  z-index: 20;
}

.teleport-core {
  position: relative;
  background: rgba(0, 212, 255, 0.2);
  border: 2px solid rgba(0, 212, 255, 0.8);
  border-radius: 25px;
  padding: 10px 20px;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.6s ease;
  backdrop-filter: blur(15px);
  overflow: hidden;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 212, 255, 0.5);
}

.teleport-energy {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.4), transparent);
  transition: left 0.8s ease;
}

.teleport-btn:hover .teleport-energy {
  left: 100%;
}

.teleport-btn:hover .teleport-core {
  background: rgba(0, 212, 255, 0.2);
  border-color: #00d4ff;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 212, 255, 0.4);
}

.teleport-field {
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 25px;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.teleport-btn:hover .teleport-field {
  opacity: 1;
  animation: teleportField 1s ease-in-out infinite;
}

/* 重力扭曲效果 */
.gravity-distortion {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.floating-medicine-card:hover .gravity-distortion {
  opacity: 1;
}

.distortion-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 50%;
  animation: distortionRing 3s ease-in-out infinite;
}

.distortion-ring:nth-child(1) {
  width: 80%;
  height: 80%;
  animation-delay: 0s;
}

.distortion-ring:nth-child(2) {
  width: 60%;
  height: 60%;
  animation-delay: 1s;
}

.distortion-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  animation-delay: 2s;
}

/* 重力加载状态 */
.gravity-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  margin: 80px 0;
  position: relative;
  z-index: 10;
}

.gravity-field {
  position: relative;
  width: 200px;
  height: 200px;
  margin-bottom: 30px;
}

.mass-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background: radial-gradient(circle, #00d4ff, #0099cc);
  border-radius: 50%;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.8);
  animation: massPulse 2s ease-in-out infinite;
}

.orbit-particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: rgba(0, 212, 255, 0.8);
  border-radius: 50%;
  transform-origin: center;
  animation: orbit 3s linear infinite;
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.6);
}

.loading-text-holo {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  animation: textPulse 2s ease-in-out infinite;
}

/* 量子错误状态 */
.quantum-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  margin: 80px 0;
  position: relative;
  z-index: 10;
}

.error-field {
  position: relative;
  margin-bottom: 30px;
}

.error-distortion {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(255, 68, 68, 0.3), transparent);
  animation: rotate 3s linear infinite;
  border-radius: 50%;
}

.error-icon {
  color: #ff4444;
  filter: drop-shadow(0 0 20px rgba(255, 68, 68, 0.6));
  animation: errorGlitch 1s ease-in-out infinite;
  position: relative;
  z-index: 2;
}

.quantum-error p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin-bottom: 20px;
  text-shadow: 0 2px 10px rgba(255, 68, 68, 0.3);
}

.quantum-retry-btn {
  background: rgba(255, 68, 68, 0.1);
  border: 2px solid rgba(255, 68, 68, 0.5);
  border-radius: 25px;
  color: #ff4444;
  padding: 12px 30px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.6s ease;
  backdrop-filter: blur(15px);
  font-size: 1rem;
}

.quantum-retry-btn:hover {
  background: rgba(255, 68, 68, 0.2);
  border-color: #ff4444;
  transform: translateY(-3px);
  box-shadow: 0 15px 30px rgba(255, 68, 68, 0.4);
}

/* 虚空空间 */
.void-space {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  margin: 80px 0;
  position: relative;
  z-index: 10;
}

.void-field {
  position: relative;
  width: 150px;
  height: 150px;
  margin-bottom: 30px;
}

.void-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  animation: voidRipple 2s ease-out infinite;
}

.void-ripple:nth-child(2) {
  animation-delay: 0.7s;
}

.void-ripple:nth-child(3) {
  animation-delay: 1.4s;
}

.void-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
  font-style: italic;
  text-shadow: 0 2px 10px rgba(255, 255, 255, 0.1);
}

/* 量子分页器 */
.quantum-pagination {
  margin-top: 80px;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 10;
}

.pagination-field {
  position: relative;
  background: rgba(0, 212, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 50px;
  padding: 20px 40px;
  border: 2px solid rgba(0, 212, 255, 0.2);
  box-shadow: 0 0 40px rgba(0, 212, 255, 0.2);
}

.pagination-field::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(0, 212, 255, 0.1), transparent);
  animation: rotate 15s linear infinite;
  pointer-events: none;
  border-radius: 50px;
}

/* 响应式设计 - 药品展示区域 */
@media (max-width: 1200px) {
  .gravity-medicine-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  
  .card-hull {
    height: 400px;
  }
  
  .medicine-image-layer {
    height: 220px;
  }
}

@media (max-width: 768px) {
  .gravity-medicine-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    padding: 0 15px;
  }
  
  .card-hull {
    height: 380px;
  }
  
  .medicine-image-layer {
    height: 200px;
  }
  
  .medicine-name {
    font-size: 1.2rem;
  }
  
  .showcase-header h2 {
    font-size: 1.8rem;
  }
}

@media (max-width: 480px) {
  .gravity-medicine-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 0 10px;
  }
  
  .card-hull {
    height: 360px;
  }
  
  .medicine-image-layer {
    height: 180px;
  }
  
  .medicine-name {
    font-size: 1.1rem;
  }
  
  .quantum-pagination {
    margin-top: 40px;
  }
  
  .pagination-field {
    padding: 15px 30px;
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* 神经网络系统完整样式 */

/* 错误状态 */
.neural-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  position: relative;
  z-index: 10;
}

.error-disturbance {
  position: relative;
  margin-bottom: 30px;
}

.disturbance-wave {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(255, 68, 68, 0.4);
  border-radius: 50%;
  animation: disturbanceWave 1s ease-out infinite;
}

.disturbance-wave:nth-child(1) { width: 60px; height: 60px; animation-delay: 0s; }
.disturbance-wave:nth-child(2) { width: 80px; height: 80px; animation-delay: 0.3s; }
.disturbance-wave:nth-child(3) { width: 100px; height: 100px; animation-delay: 0.6s; }

.error-icon-neural {
  color: #ff4444;
  font-size: 48px;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 0 20px rgba(255, 68, 68, 0.6));
}

.error-text-neural {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin-bottom: 20px;
  text-align: center;
}

.neural-retry-btn {
  position: relative;
  background: rgba(255, 68, 68, 0.1);
  border: 2px solid rgba(255, 68, 68, 0.5);
  border-radius: 25px;
  color: #ff4444;
  padding: 12px 30px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.6s ease;
  backdrop-filter: blur(15px);
  overflow: hidden;
}

.btn-neural-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 68, 68, 0.3), transparent);
  transition: left 0.8s ease;
}

.neural-retry-btn:hover .btn-neural-glow {
  left: 100%;
}

/* 神经内容矩阵 */
.neural-content-matrix {
  width: 100%;
  position: relative;
}

/* 全息标题投影 */
.holographic-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 10;
}

.holo-title-container {
  position: relative;
  margin-bottom: 60px;
}

.title-scanner-line {
  position: absolute;
  top: -10px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  animation: scannerMove 3s ease-in-out infinite;
}

.neural-main-title {
  font-size: 4rem;
  font-weight: 900;
  color: #ffffff !important;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  text-shadow: 
    0 0 10px rgba(255, 255, 255, 0.8),
    0 0 20px rgba(0, 212, 255, 0.6),
    0 0 30px rgba(0, 212, 255, 0.4) !important;
  filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.5));
  -webkit-text-fill-color: #ffffff !important;
}

.neural-main-title * {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
}

.title-char {
  display: inline-block;
  animation: charFloat 2s ease-in-out infinite;
  text-shadow: 
    0 0 15px rgba(255, 255, 255, 0.9),
    0 0 25px rgba(0, 212, 255, 0.7),
    0 0 35px rgba(0, 212, 255, 0.5) !important;
  color: #ffffff !important;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6));
  -webkit-text-fill-color: #ffffff !important;
}

.subtitle-projection {
  font-size: 1.5rem;
  color: #ffffff !important;
  text-shadow: 
    0 0 8px rgba(255, 255, 255, 0.8),
    0 0 15px rgba(0, 212, 255, 0.5),
    0 0 20px rgba(0, 212, 255, 0.3) !important;
  margin-bottom: 40px;
  font-weight: 600;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.4));
}


/* 信息流矩阵 */
.info-flow-matrix {
  display: grid;
  grid-template-columns: 1fr;
  gap: 60px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 主信息流 */
.primary-info-stream {
  background: rgba(10, 15, 30, 0.9);
  backdrop-filter: blur(30px);
  border-radius: 30px;
  padding: 50px;
  border: 2px solid rgba(0, 212, 255, 0.2);
  box-shadow: 0 0 50px rgba(0, 212, 255, 0.1);
  position: relative;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.9) !important;
}

.primary-info-stream * {
  color: rgba(255, 255, 255, 0.9) !important;
}

.primary-info-stream p {
  color: rgba(255, 255, 255, 0.9) !important;
}

.primary-info-stream div {
  color: rgba(255, 255, 255, 0.9) !important;
}

.stream-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
}

.stream-icon {
  color: #00d4ff;
  animation: iconGlow 2s ease-in-out infinite;
}

.stream-title {
  color: #00d4ff;
  font-size: 1.4rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.stream-flow-indicator {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  margin-left: 20px;
  animation: flowIndicator 2s ease-in-out infinite;
}

.info-content-neural {
  position: relative;
  z-index: 2;
  color: rgba(255, 255, 255, 0.9) !important;
}

.info-content-neural * {
  color: rgba(255, 255, 255, 0.9) !important;
}

.info-content-neural p {
  color: rgba(255, 255, 255, 0.9) !important;
}

.info-content-neural div {
  color: rgba(255, 255, 255, 0.9) !important;
}

.neural-text-block {
  color: rgba(255, 255, 255, 0.9) !important;
  line-height: 1.8;
}

.neural-text-block * {
  color: rgba(255, 255, 255, 0.9) !important;
}

.neural-paragraph {
  font-size: 1.1rem;
  margin-bottom: 20px;
  position: relative;
  padding-left: 20px;
  color: rgba(255, 255, 255, 0.9) !important;
}

.neural-paragraph::before {
  content: '▶';
  position: absolute;
  left: 0;
  color: #00d4ff;
  animation: caretBlink 1.5s ease-in-out infinite;
}


/* 神经传导行动区 */
.neural-action-zone {
  text-align: center;
  margin-top: 60px;
}

.action-neural-field {
  position: relative;
  display: inline-block;
}

.neural-explore-btn {
  position: relative;
  text-decoration: none;
  display: inline-block;
  transition: all 0.6s ease;
}

.btn-neural-core {
  position: relative;
  background: rgba(0, 212, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 3px solid rgba(0, 212, 255, 0.4);
  border-radius: 50px;
  padding: 20px 50px;
  overflow: hidden;
  transition: all 0.6s ease;
}

.btn-neural-text {
  color: #00d4ff;
  font-size: 1.3rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  position: relative;
  z-index: 2;
}

.btn-data-flow {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
}

.flow-bit {
  width: 3px;
  height: 3px;
  background: rgba(0, 212, 255, 0.8);
  border-radius: 50%;
  animation: flowBit 1.5s ease-in-out infinite;
}

.flow-bit:nth-child(odd) { animation-delay: 0.2s; }
.flow-bit:nth-child(even) { animation-delay: 0.4s; }

.btn-neural-aura {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 2px solid rgba(0, 212, 255, 0.3);
  border-radius: 55px;
  opacity: 0;
  transition: opacity 0.6s ease;
  animation: auraGlow 2s ease-in-out infinite;
}

.neural-explore-btn:hover .btn-neural-aura {
  opacity: 1;
}

.btn-quantum-trails {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  pointer-events: none;
}

.trail {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 50%;
  animation: trailRotate 3s linear infinite;
}

.trail:nth-child(2) { animation-delay: 1s; animation-direction: reverse; }
.trail:nth-child(3) { animation-delay: 2s; }

/* 神经网络动画关键帧 */
@keyframes neuralPulse {
  0%, 100% { transform: scale(var(--pulse-scale, 1)); opacity: 0.6; }
  50% { transform: scale(calc(var(--pulse-scale, 1) * 1.5)); opacity: 1; }
}

@keyframes dataFlow {
  0% { stroke-dashoffset: 0; opacity: 0.3; }
  50% { opacity: 0.8; }
  100% { stroke-dashoffset: -30; opacity: 0.3; }
}

@keyframes quantumFloat {
  0%, 100% { transform: translateY(0) translateX(0); opacity: 0.4; }
  25% { transform: translateY(var(--float-amplitude, -30px)) translateX(20px); opacity: 0.8; }
  75% { transform: translateY(calc(var(--float-amplitude, -30px) * -1)) translateX(-20px); opacity: 0.6; }
}

@keyframes neuralPulseRing {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
}

@keyframes textGlow {
  0%, 100% { text-shadow: 0 0 10px rgba(0, 212, 255, 0.5); }
  50% { text-shadow: 0 0 20px rgba(0, 212, 255, 0.8), 0 0 30px rgba(0, 212, 255, 0.4); }
}

@keyframes disturbanceWave {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
}

@keyframes scannerMove {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

@keyframes charFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}


@keyframes iconGlow {
  0%, 100% { filter: drop-shadow(0 0 10px rgba(0, 212, 255, 0.5)); }
  50% { filter: drop-shadow(0 0 20px rgba(0, 212, 255, 0.8)); }
}

@keyframes flowIndicator {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes caretBlink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.3; }
}


@keyframes flowBit {
  0%, 100% { transform: scale(0.8); opacity: 0.4; }
  50% { transform: scale(1.2); opacity: 1; }
}

@keyframes auraGlow {
  0%, 100% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.05); opacity: 0.7; }
}

@keyframes trailRotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 神经网络响应式设计 */
@media (max-width: 1024px) {
  .neural-about-section { padding: 120px 0; }
  .neural-container { padding: 0 15px; }
  .neural-main-title { 
    font-size: 3.2rem !important;
    text-shadow: 
      0 0 12px rgba(255, 255, 255, 0.9),
      0 0 22px rgba(0, 212, 255, 0.7),
      0 0 32px rgba(0, 212, 255, 0.5) !important;
  }
  .subtitle-projection {
    font-size: 1.3rem !important;
    text-shadow: 
      0 0 10px rgba(255, 255, 255, 0.9),
      0 0 18px rgba(0, 212, 255, 0.6) !important;
  }
}

@media (max-width: 768px) {
  .neural-about-section { padding: 80px 0; }
  .neural-main-title { 
    font-size: 2.8rem !important;
    text-shadow: 
      0 0 15px rgba(255, 255, 255, 1),
      0 0 25px rgba(0, 212, 255, 0.8),
      0 0 35px rgba(0, 212, 255, 0.6) !important;
  }
  .subtitle-projection {
    font-size: 1.2rem !important;
    text-shadow: 
      0 0 12px rgba(255, 255, 255, 1),
      0 0 20px rgba(0, 212, 255, 0.7) !important;
  }
  .primary-info-stream { padding: 30px; }
}

@keyframes showcaseGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes gradientText {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes cardFloat {
  0%, 100% {
    transform: translateY(0px) rotateX(0deg);
  }
  33% {
    transform: translateY(-5px) rotateX(1deg);
  }
  66% {
    transform: translateY(2px) rotateX(-1deg);
  }
}

@keyframes aboutGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
}

@keyframes aboutCardFloat {
  0%, 100% {
    transform: translateY(0px) rotateX(0deg) rotateY(0deg);
  }
  25% {
    transform: translateY(-8px) rotateX(2deg) rotateY(1deg);
  }
  50% {
    transform: translateY(0px) rotateX(0deg) rotateY(-1deg);
  }
  75% {
    transform: translateY(4px) rotateX(-1deg) rotateY(1deg);
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 40px 50px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.4);
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.header-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.03) 0%,
    rgba(139, 92, 246, 0.05) 50%,
    rgba(236, 72, 153, 0.03) 100%);
  pointer-events: none;
}

.header-content::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(59, 130, 246, 0.02), transparent);
  animation: rotate 20s linear infinite;
  pointer-events: none;
}

.header-text {
  position: relative;
}

.header-text::before {
  content: '';
  position: absolute;
  left: -20px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 60px;
  background: linear-gradient(45deg, #3a8ee6, #42b983);
  border-radius: 2px;
}

.header-text h2 {
  font-size: 3rem;
  background: linear-gradient(135deg,
    #3b82f6 0%,
    #8b5cf6 25%,
    #ec4899 50%,
    #f59e0b 75%,
    #10b981 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 15px;
  font-weight: 900;
  letter-spacing: -0.02em;
  position: relative;
  animation: gradientText 4s ease-in-out infinite;
  text-shadow: 0 0 30px rgba(59, 130, 246, 0.3);
}

.header-text h2::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 80px;
  height: 3px;
  background: linear-gradient(45deg, #3a8ee6, #42b983);
  border-radius: 2px;
  opacity: 0.7;
}

.header-text p {
  color: #666;
  font-size: 1.1rem;
  margin: 0;
  font-weight: 500;
  opacity: 0.9;
}

.view-all-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #3a8ee6 0%, #42b983 100%);
  color: white;
  padding: 14px 28px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 6px 20px rgba(58, 142, 230, 0.3);
  position: relative;
  overflow: hidden;
}

.view-all-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s ease;
}

.view-all-btn:hover::before {
  left: 100%;
}

.view-all-btn:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 12px 35px rgba(58, 142, 230, 0.4);
}

.view-all-btn svg {
  transition: transform 0.3s ease;
}

.view-all-btn:hover svg {
  transform: translateX(4px);
}

.medicine-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
  perspective: 1000px;
}

.medicine-item {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12),
              0 0 0 1px rgba(255, 255, 255, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  min-height: 400px;
  position: relative;
  transform-style: preserve-3d;
  animation: cardFloat 6s ease-in-out infinite;
}

.medicine-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 25%,
    rgba(236, 72, 153, 0.02) 50%,
    rgba(251, 191, 36, 0.04) 75%,
    rgba(34, 197, 94, 0.02) 100%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.medicine-item::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(59, 130, 246, 0.01), transparent);
  animation: rotate 15s linear infinite;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.medicine-item:hover {
  transform: translateY(-15px) rotateX(5deg) rotateY(5deg) scale(1.02);
  box-shadow: 0 40px 100px rgba(59, 130, 246, 0.25),
              0 0 0 1px rgba(255, 255, 255, 0.6),
              inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.medicine-item:hover::before,
.medicine-item:hover::after {
  opacity: 1;
}

.item-image {
  width: 100%;
  height: 220px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 20px 20px 0 0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  filter: brightness(0.9) contrast(1.1) saturate(1.2);
}

.medicine-item:hover .item-image img {
  transform: scale(1.1) rotate(2deg);
  filter: brightness(1.1) contrast(1.2) saturate(1.4);
}

.medicine-item:hover .item-image img {
  transform: scale(1.1);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.medicine-item:hover .image-overlay {
  opacity: 1;
}

.quick-view-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #3a8ee6;
  border: none;
  padding: 10px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.quick-view-btn:hover {
  background: #3a8ee6;
  color: white;
  transform: scale(1.1);
}

.item-content {
  flex: 1;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.item-title {
  font-size: 1.5rem;
  background: linear-gradient(135deg, #2c3e50 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
  font-weight: 800;
  line-height: 1.3;
  flex: 1;
  margin-right: 15px;
  margin-bottom: 10px;
}

.item-category {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
  animation: gradientText 3s ease-in-out infinite;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.item-details {
  flex: 1;
  margin-bottom: 15px;
}

.detail-row {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
}

.detail-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
  min-width: 80px;
  margin-right: 10px;
}

.detail-value {
  font-size: 0.9rem;
  color: #333;
  flex: 1;
  line-height: 1.4;
}

.description-row .detail-value {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-actions {
  display: flex;
  justify-content: flex-end;
}

.detail-link {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  background-size: 200% 200%;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
  backdrop-filter: blur(10px);
}

.detail-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s ease;
}

.detail-link:hover::before {
  left: 100%;
}

.detail-link:hover {
  background-position: 100% 100%;
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.5);
  border-color: rgba(255, 255, 255, 0.4);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .header-content {
    padding: 25px 30px;
  }

  .header-text h2 {
    font-size: 2.1rem;
  }
}

@media (max-width: 768px) {
  .medicine-showcase {
    padding: 80px 15px;
  }

  .medicine-list {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .medicine-item {
    min-height: 350px;
  }

  .item-image {
    height: 180px;
  }

  .item-content {
    padding: 25px;
  }

  .header-content {
    flex-direction: column;
    gap: 25px;
    text-align: center;
    padding: 25px 20px;
  }

  .header-text::before {
    display: none;
  }

  .header-text h2 {
    font-size: 2.5rem;
  }

  .header-text h2::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .view-all-btn {
    padding: 12px 24px;
    font-size: 0.95rem;
  }

  .medicine-item {
    flex-direction: column;
    min-height: auto;
  }

  .item-image {
    width: 100%;
    height: 200px;
  }

  .item-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .item-title {
    margin-right: 0;
  }

  .detail-row {
    flex-direction: column;
    gap: 4px;
  }

  .detail-label {
    min-width: auto;
    margin-right: 0;
  }
}

/* 神经网络关于我们区域 - 未来科技设计 */
.neural-about-section {
  background: radial-gradient(ellipse at center, #0a0a0f 0%, #050508 50%, #000 100%);
  padding: 200px 0;
  position: relative;
  overflow: hidden;
  min-height: 100vh;
}

/* 神经网络背景系统 */
.neural-network-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
}

.neural-nodes {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.neural-node {
  position: absolute;
  width: 8px;
  height: 8px;
  background: var(--neural-color, #00d4ff);
  border-radius: 50%;
  box-shadow: 0 0 20px var(--neural-color, #00d4ff);
  animation: neuralPulse 3s ease-in-out infinite;
  transform: scale(var(--pulse-scale, 1));
}

.neural-connections {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.connections-svg {
  width: 100%;
  height: 100%;
}

.connection-line {
  fill: none;
  stroke: rgba(0, 212, 255, 0.3);
  stroke-width: 1px;
  stroke-dasharray: 5,10;
  animation: dataFlow 4s linear infinite;
}

.quantum-particles {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.quantum-particle {
  position: absolute;
  width: var(--particle-size, 3px);
  height: var(--particle-size, 3px);
  background: radial-gradient(circle, rgba(0, 212, 255, 0.8), transparent);
  border-radius: 50%;
  animation: quantumFloat 8s ease-in-out infinite;
}

.neural-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 10;
}

/* 加载状态 - 神经脉冲 */
.neural-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  position: relative;
  z-index: 10;
}

.pulse-core {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 30px;
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(0, 212, 255, 0.4);
  border-radius: 50%;
  animation: neuralPulseRing 2s ease-out infinite;
}

.pulse-ring:nth-child(1) { width: 40px; height: 40px; animation-delay: 0s; }
.pulse-ring:nth-child(2) { width: 60px; height: 60px; animation-delay: 0.5s; }
.pulse-ring:nth-child(3) { width: 80px; height: 80px; animation-delay: 1s; }
.pulse-ring:nth-child(4) { width: 100px; height: 100px; animation-delay: 1.5s; }

.loading-text-neural {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  animation: textGlow 2s ease-in-out infinite;
}

.about-content-area::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 25%,
    rgba(236, 72, 153, 0.02) 50%,
    rgba(251, 191, 36, 0.04) 75%,
    rgba(34, 197, 94, 0.02) 100%);
  pointer-events: none;
}

.about-content-area::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(59, 130, 246, 0.01), transparent);
  animation: rotate 20s linear infinite;
  pointer-events: none;
}

.content-header {
  margin-bottom: 60px;
  text-align: center;
  position: relative;
  z-index: 2;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 30px;
  animation: gradientText 4s ease-in-out infinite;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.main-title {
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.1;
  background: linear-gradient(135deg,
    #3b82f6 0%,
    #8b5cf6 25%,
    #ec4899 50%,
    #f59e0b 75%,
    #10b981 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: gradientText 5s ease-in-out infinite;
  text-shadow: 0 0 40px rgba(59, 130, 246, 0.3);
  letter-spacing: -0.02em;
  position: relative;
}

.main-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  border-radius: 2px;
  animation: gradientText 3s ease-in-out infinite;
}

.subtitle {
  font-size: 1.4rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;
  opacity: 0.9;
}

.content-body {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.description-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #475569;
}

.description-text p {
  margin-bottom: 20px;
}

.highlight-features {
  display: flex;
  gap: 40px;
  padding: 40px;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.08) 0%,
    rgba(139, 92, 246, 0.1) 25%,
    rgba(236, 72, 153, 0.08) 50%,
    rgba(251, 191, 36, 0.1) 75%,
    rgba(34, 197, 94, 0.08) 100%);
  background-size: 200% 200%;
  animation: gradientText 6s ease-in-out infinite;
  border-radius: 25px;
  border: 2px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 20px 40px rgba(59, 130, 246, 0.1);
  position: relative;
  overflow: hidden;
}

.highlight-features::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 50%,
    rgba(255, 255, 255, 0.1) 100%);
  pointer-events: none;
}

.feature-item {
  text-align: center;
  flex: 1;
  position: relative;
  z-index: 2;
  padding: 20px;
  border-radius: 20px;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.feature-item:hover {
  transform: translateY(-10px) scale(1.05);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.feature-number {
  font-size: 3.5rem;
  font-weight: 900;
  background: linear-gradient(135deg,
    #3b82f6 0%,
    #8b5cf6 50%,
    #ec4899 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 12px;
  animation: gradientText 4s ease-in-out infinite;
  text-shadow: 0 0 30px rgba(59, 130, 246, 0.3);
  line-height: 1;
}

.feature-label {
  font-size: 1.1rem;
  color: #475569;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.core-values {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin: 50px 0;
}

.value-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 35px 25px;
  border-radius: 25px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
}

.value-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 50%,
    rgba(236, 72, 153, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.6s ease;
  pointer-events: none;
}

.value-card:hover {
  transform: translateY(-15px) rotateY(5deg) scale(1.05);
  box-shadow: 0 40px 100px rgba(59, 130, 246, 0.25);
}

.value-card:hover::before {
  opacity: 1;
}

.value-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: white;
  animation: gradientText 4s ease-in-out infinite;
  box-shadow: 0 15px 35px rgba(59, 130, 246, 0.3);
  position: relative;
  z-index: 2;
  transition: all 0.6s ease;
}

.value-card:hover .value-icon {
  transform: scale(1.1) rotateY(180deg);
  box-shadow: 0 25px 50px rgba(59, 130, 246, 0.5);
}

.value-card h4 {
  font-size: 1.4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2d3748 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 15px;
  position: relative;
  z-index: 2;
}

.value-card p {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  position: relative;
  z-index: 2;
}

.action-area {
  display: flex;
  justify-content: center;
  margin-top: 50px;
}

.learn-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  color: white;
  padding: 18px 40px;
  border-radius: 35px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.2rem;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  animation: gradientText 4s ease-in-out infinite;
}

.learn-more-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s ease;
}

.learn-more-btn:hover::before {
  left: 100%;
}

.learn-more-btn:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 25px 60px rgba(59, 130, 246, 0.6);
  border-color: rgba(255, 255, 255, 0.4);
}

.learn-more-btn svg {
  transition: transform 0.6s ease;
}

.learn-more-btn:hover svg {
  transform: translateX(6px) rotate(45deg);
}



/* 关于我们响应式设计 */
@media (max-width: 1024px) {
  .about-content-area {
    padding: 50px 40px;
    max-width: 800px;
  }

  .main-title {
    font-size: 2.2rem;
  }

  .highlight-features {
    flex-direction: column;
    gap: 20px;
  }

  .core-values {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}

@media (max-width: 768px) {
  .about-section {
    padding: 80px 0;
  }

  .about-container {
    padding: 0 15px;
  }

  .about-content-area {
    padding: 40px 25px;
    max-width: 100%;
  }

  .main-title {
    font-size: 2.5rem;
  }

  .subtitle {
    font-size: 1.2rem;
  }

  .content-body {
    gap: 40px;
  }

  .highlight-features {
    flex-direction: column;
    gap: 25px;
    padding: 30px 25px;
  }

  .feature-number {
    font-size: 3rem;
  }

  .core-values {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .value-card {
    padding: 30px 25px;
  }

  .value-icon {
    width: 70px;
    height: 70px;
  }
}

@media (max-width: 480px) {
  .about-content-area {
    padding: 30px 20px;
  }

  .main-title {
    font-size: 1.7rem;
  }

  .header-badge {
    padding: 6px 16px;
    font-size: 0.85rem;
  }

  .highlight-features {
    padding: 20px 15px;
  }

  .feature-number {
    font-size: 1.8rem;
  }

  .core-values {
    gap: 12px;
  }

  .value-card {
    padding: 20px;
  }
}




/* 媒体查询，响应式设计 */
@media (max-width: 1200px) {
  .product-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .about-content {
    flex-direction: column;
  }
}

/* 加载中和错误样式 */
.loading-wrapper,
.error-wrapper {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  margin-top: 80px;
  gap: 10px;
}

.loading-icon {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.banner-link {
  display: block;
  height: 100%;
  width: 100%;
  text-decoration: none;
}

.quick-view-btn {
  background-color: white;
  color: #333;
  border: none;
  padding: 10px 15px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
}

.quick-view-btn:hover {
  background-color: #3a8ee6;
  color: white;
}

.detail-view-btn {
  background-color: white;
  color: #333;
  border: none;
  padding: 10px 15px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  text-decoration: none;
}

.detail-view-btn:hover {
  background-color: #3a8ee6;
  color: white;
}



.about-us:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #3a8ee6, #42b983);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(0deg, rgba(58, 142, 230, 0.9) 0%, rgba(58, 142, 230, 0) 100%);
  padding: 30px 20px 15px;
  color: white;
}

.overlay-content {
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
}

/* 添加加载和错误状态样式 */
.loading-container,
.error-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  margin: 50px 0;
}

.loading-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.loading-icon {
  color: #3a8ee6;
  animation: rotate 2s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.retry-btn {
  margin-top: 20px;
  background-color: #3a8ee6;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
}

.retry-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-3px);
}

.pagination-container {
  margin-top: 50px;
  display: flex;
  justify-content: center;
}

/* 粒子爆炸和波浪扭曲动画 */
@keyframes particleExplode {
  0% {
    transform: translate(-50%, -50%) scale(0) rotate(0deg);
    opacity: 0;
    background: radial-gradient(circle, #60a5fa 0%, #3b82f6 30%, transparent 70%);
  }
  25% {
    transform: translate(-50%, -50%) scale(10) rotate(90deg);
    opacity: 0.8;
    background: radial-gradient(circle, #8b5cf6 0%, #7c3aed 30%, transparent 70%);
  }
  50% {
    transform: translate(-50%, -50%) scale(25) rotate(180deg);
    opacity: 1;
    background: radial-gradient(circle, #ec4899 0%, #db2777 30%, transparent 70%);
  }
  75% {
    transform: translate(-50%, -50%) scale(40) rotate(270deg);
    opacity: 0.6;
    background: radial-gradient(circle, #fbbf24 0%, #f59e0b 30%, transparent 70%);
  }
  100% {
    transform: translate(-50%, -50%) scale(60) rotate(360deg);
    opacity: 0;
    background: radial-gradient(circle, #22c55e 0%, #16a34a 30%, transparent 70%);
  }
}

@keyframes waveDistortion {
  0% {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
    filter: hue-rotate(0deg) saturate(1) brightness(1);
  }
  20% {
    clip-path: polygon(0% 10%, 100% 0%, 100% 90%, 0% 100%);
    filter: hue-rotate(72deg) saturate(1.2) brightness(1.1);
  }
  40% {
    clip-path: polygon(0% 20%, 100% 10%, 100% 80%, 0% 90%);
    filter: hue-rotate(144deg) saturate(1.4) brightness(1.2);
  }
  60% {
    clip-path: polygon(0% 30%, 100% 20%, 100% 70%, 0% 80%);
    filter: hue-rotate(216deg) saturate(1.6) brightness(1.3);
  }
  80% {
    clip-path: polygon(0% 40%, 100% 30%, 100% 60%, 0% 70%);
    filter: hue-rotate(288deg) saturate(1.8) brightness(1.4);
  }
  100% {
    clip-path: polygon(0% 50%, 100% 50%, 100% 50%, 0% 50%);
    filter: hue-rotate(360deg) saturate(2) brightness(1.5);
  }
}

@keyframes waveDistortionReverse {
  0% {
    clip-path: polygon(0% 50%, 100% 50%, 100% 50%, 0% 50%);
    filter: hue-rotate(360deg) saturate(2) brightness(1.5);
  }
  20% {
    clip-path: polygon(0% 40%, 100% 30%, 100% 60%, 0% 70%);
    filter: hue-rotate(288deg) saturate(1.8) brightness(1.4);
  }
  40% {
    clip-path: polygon(0% 30%, 100% 20%, 100% 70%, 0% 80%);
    filter: hue-rotate(216deg) saturate(1.6) brightness(1.3);
  }
  60% {
    clip-path: polygon(0% 20%, 100% 10%, 100% 80%, 0% 90%);
    filter: hue-rotate(144deg) saturate(1.4) brightness(1.2);
  }
  80% {
    clip-path: polygon(0% 10%, 100% 0%, 100% 90%, 0% 100%);
    filter: hue-rotate(72deg) saturate(1.2) brightness(1.1);
  }
  100% {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
    filter: hue-rotate(0deg) saturate(1) brightness(1);
  }
}

/* 3D透视效果增强 */
.fragment-carousel {
  perspective: 2000px;
  transform-style: preserve-3d;
}

.fragment-carousel-item {
  transform-style: preserve-3d;
  backface-visibility: hidden;
}

/* 更多随机位移变量 */
.fragment:nth-child(26) { --random-x: -200px; --random-y: -100px; }
.fragment:nth-child(27) { --random-x: -100px; --random-y: -150px; }
.fragment:nth-child(28) { --random-x: 50px; --random-y: -200px; }
.fragment:nth-child(29) { --random-x: 200px; --random-y: -150px; }
.fragment:nth-child(30) { --random-x: 350px; --random-y: -100px; }
.fragment:nth-child(31) { --random-x: -350px; --random-y: 50px; }
.fragment:nth-child(32) { --random-x: -200px; --random-y: 100px; }
.fragment:nth-child(33) { --random-x: -50px; --random-y: 150px; }
.fragment:nth-child(34) { --random-x: 100px; --random-y: 200px; }
.fragment:nth-child(35) { --random-x: 300px; --random-y: 150px; }
.fragment:nth-child(36) { --random-x: -400px; --random-y: 200px; }
.fragment:nth-child(37) { --random-x: -250px; --random-y: 300px; }
.fragment:nth-child(38) { --random-x: 0px; --random-y: 350px; }
.fragment:nth-child(39) { --random-x: 250px; --random-y: 300px; }
.fragment:nth-child(40) { --random-x: 400px; --random-y: 200px; }
.fragment:nth-child(41) { --random-x: -300px; --random-y: -300px; }
.fragment:nth-child(42) { --random-x: -150px; --random-y: -350px; }
.fragment:nth-child(43) { --random-x: 0px; --random-y: -400px; }
.fragment:nth-child(44) { --random-x: 150px; --random-y: -350px; }
.fragment:nth-child(45) { --random-x: 300px; --random-y: -300px; }
.fragment:nth-child(46) { --random-x: -450px; --random-y: 0px; }
.fragment:nth-child(47) { --random-x: -300px; --random-y: 50px; }
.fragment:nth-child(48) { --random-x: -150px; --random-y: 100px; }

/* ===== 支付弹窗样式 ===== */
.payment-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.payment-dialog {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 450px;
  width: 90%;
  overflow: hidden;
  animation: slideUp 0.4s ease;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.payment-header {
  background: linear-gradient(135deg, #007AFF 0%, #0051D5 100%);
  color: white;
  padding: 24px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.payment-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: rotate 10s linear infinite;
}

.payment-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  z-index: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 1;
  color: white;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.payment-content {
  padding: 35px 30px;
}

.product-info {
  text-align: center;
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 2px dashed rgba(0, 122, 255, 0.2);
}

.product-info h4 {
  margin: 0 0 12px 0;
  font-size: 1.3rem;
  color: #333;
  font-weight: 600;
}

.price {
  font-size: 2.2rem;
  color: #007AFF;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
}

.qr-code-container {
  text-align: center;
}

.qr-code {
  width: 220px;
  height: 220px;
  margin: 0 auto 20px;
  padding: 15px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  display: block;
  border: 3px solid #007AFF;
  transition: all 0.3s;
}

.qr-code:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px rgba(0, 122, 255, 0.3);
}

.qr-tip {
  color: #666;
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 576px) {
  .payment-dialog {
    width: 95%;
    max-width: none;
  }
  
  .payment-header {
    padding: 20px 20px;
  }
  
  .payment-header h3 {
    font-size: 1.3rem;
  }
  
  .payment-content {
    padding: 25px 20px;
  }
  
  .product-info h4 {
    font-size: 1.1rem;
  }
  
  .price {
    font-size: 1.8rem;
  }
  
  .qr-code {
    width: 180px;
    height: 180px;
  }
}

</style>
